import type { Position, EntanglingRootsEffect, Corpse } from "@/types/game.types"
import type { Enemy, EnemySpriteConfig, EnemyAnimationState } from "@/types/enemy.types"
import type { Tower } from "@/types/tower.types"
import type { Hero, ActiveSkillEffect } from "@/types/hero.types"
import type { Projectile } from "@/types/projectile.types"
import type { SummonedUnit } from "@/types/summon.types"
import type { BarracksSoldier, SoldierAnimationState, SoldierFacingDirection } from "@/types/barracks.types"
import type { ActiveItemEffect } from "@/types/item.types"
import type { UpgradeState } from "@/types/upgrade.types"
import type { LevelTilemap, LevelDecoration, ObjectDecoration } from "@/types/tileset.types"
import {
  TILESET_SHEETS,
  SPRITE_TILE_SIZE,
  DECORATION_DEFS,
} from "@/types/tileset.types"
import { gameSettings } from "@/data/game-settings"
import { tileToWorld } from "./tile-utils"
import { enemyTypes } from "@/data/enemy-types"
import { heroLevelConfig } from "@/data/hero-types"
import { TILE_SIZE, SUBTILE_SIZE } from "@/data/game-settings"

// Summon fade-in animation duration in milliseconds
const SUMMON_FADE_IN_DURATION = 500

// Calculate opacity for summon fade-in animation (0-1)
const getSummonFadeInOpacity = (summon: SummonedUnit): number => {
  const elapsed = gameNow() - summon.spawnTime
  if (elapsed >= SUMMON_FADE_IN_DURATION) return 1
  return elapsed / SUMMON_FADE_IN_DURATION
}


// ===== ZOOM-INVARIANT OVERLAYS =====
// Health bars, status icons, selection brackets and the like are informational
// UI that happens to live in world space. Drawn plainly they scale with the
// camera, so zooming in on a fight blew them up along with the sprites. The
// canvas sets an overlay scale each frame and these helpers draw at a constant
// on-screen size instead, while staying anchored to their entity.
let overlayScale = 1

/**
 * @param scale multiplier applied to world-space UI. Pass 1/zoom to hold a
 * constant on-screen size.
 */
export const setOverlayScale = (scale: number): void => {
  overlayScale = scale > 0 ? scale : 1
}

/** Run `draw` scaled about `anchor`, so the overlay keeps its screen size. */
const withOverlayScale = (
  ctx: CanvasRenderingContext2D,
  anchor: Position,
  draw: () => void,
): void => {
  if (overlayScale === 1) {
    draw()
    return
  }
  ctx.save()
  ctx.translate(anchor.x, anchor.y)
  ctx.scale(overlayScale, overlayScale)
  ctx.translate(-anchor.x, -anchor.y)
  draw()
  ctx.restore()
}

const heroImages: Record<string, HTMLImageElement> = {}
const enemyImages: Record<string, HTMLImageElement> = {}
const towerImages: Record<string, HTMLImageElement> = {}
const towerSpriteImages: Record<string, HTMLImageElement> = {}
const enemySpriteImages: Record<string, HTMLImageElement> = {}
const summonSpriteImages: Record<string, HTMLImageElement> = {}
const tilesetImages: Record<string, HTMLImageElement> = {}
const objectDecorationImages: Record<string, HTMLImageElement> = {}
const statusEffectIconImages: Record<string, HTMLImageElement> = {}
let endMarkerImage: HTMLImageElement | null = null

/**
 * Load a status effect icon image (cached)
 */
const loadStatusEffectIcon = (path: string): HTMLImageElement => {
  if (!statusEffectIconImages[path]) {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.src = path
    statusEffectIconImages[path] = img
  }
  return statusEffectIconImages[path]
}

/**
 * Load an object decoration image (cached)
 */
const loadObjectDecorationImage = (imagePath: string): HTMLImageElement => {
  if (!objectDecorationImages[imagePath]) {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.src = imagePath
    objectDecorationImages[imagePath] = img
  }
  return objectDecorationImages[imagePath]
}

const loadEndMarkerImage = (): HTMLImageElement => {
  if (!endMarkerImage) {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.src = "/images/biomes/end-marker.png"
    endMarkerImage = img
  }
  return endMarkerImage
}

// ============================================
// STATUS EFFECT SYSTEM
// ============================================

interface StatusEffectIcon {
  type: 'shrine-buff' | 'slow-debuff'
  iconPath: string
}

const STATUS_EFFECT_ICONS = {
  SHRINE_BUFF: '/images/status/status-heroism.png',
  SLOW_DEBUFF: '/images/status/status-slowed.png',
} as const

// ============================================
// TOWER SPRITE CONFIGURATION SYSTEM
// ============================================

interface TowerSpriteAnimation {
  sheet: string
  frameWidth: number
  frameHeight: number
  frameCount: number
}

interface TowerSpriteConfig {
  build: TowerSpriteAnimation           // Building animation (level 1)
  upgrade: TowerSpriteAnimation[]       // Upgrade animations (index 0 = to level 2, etc.)
  idle: TowerSpriteAnimation[]          // Idle sprites per level (index 0 = level 1, etc.)
  menuIcon: string                      // Build menu icon path (pre-extracted)
  scale: number
  offsetY: number
}

const archerTowerSpriteConfig: TowerSpriteConfig = {
  build: {
    sheet: "/images/towers/archer/1 Upgrade/1.png",
    frameWidth: 70,
    frameHeight: 130,
    frameCount: 4,
  },
  upgrade: [
    { sheet: "/images/towers/archer/1 Upgrade/2.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/archer/1 Upgrade/3.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/archer/1 Upgrade/4.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/archer/1 Upgrade/5.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/archer/1 Upgrade/6.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/archer/1 Upgrade/7.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
  ],
  idle: [
    { sheet: "/images/towers/archer/2 Idle/1.png", frameWidth: 70, frameHeight: 130, frameCount: 1 },
    { sheet: "/images/towers/archer/2 Idle/2.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/archer/2 Idle/3.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/archer/2 Idle/4.png", frameWidth: 70, frameHeight: 130, frameCount: 6 },
    { sheet: "/images/towers/archer/2 Idle/5.png", frameWidth: 70, frameHeight: 130, frameCount: 6 },
    { sheet: "/images/towers/archer/2 Idle/6.png", frameWidth: 70, frameHeight: 130, frameCount: 6 },
    { sheet: "/images/towers/archer/2 Idle/7.png", frameWidth: 70, frameHeight: 130, frameCount: 6 },
  ],
  menuIcon: "/images/towers/archer/icon.png",
  scale: 0.65,  // Scale down to fit ~45px width on 32px tiles
  offsetY: -20,  // Offset upward since sprites are taller than wide
}

const mageTowerSpriteConfig: TowerSpriteConfig = {
  build: {
    sheet: "/images/towers/mage/1 Upgrade/1.png",
    frameWidth: 70,
    frameHeight: 130,
    frameCount: 4,
  },
  upgrade: [
    { sheet: "/images/towers/mage/1 Upgrade/2.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/mage/1 Upgrade/3.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/mage/1 Upgrade/4.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/mage/1 Upgrade/5.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/mage/1 Upgrade/6.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/mage/1 Upgrade/7.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
  ],
  idle: [
    { sheet: "/images/towers/mage/2 Idle/1.png", frameWidth: 70, frameHeight: 130, frameCount: 1 },
    { sheet: "/images/towers/mage/2 Idle/2.png", frameWidth: 70, frameHeight: 130, frameCount: 1 },
    { sheet: "/images/towers/mage/2 Idle/3.png", frameWidth: 70, frameHeight: 130, frameCount: 1 },
    { sheet: "/images/towers/mage/2 Idle/4.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/mage/2 Idle/5.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/mage/2 Idle/6.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/mage/2 Idle/7.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
  ],
  menuIcon: "/images/towers/mage/icon.png",
  scale: 0.65,
  offsetY: -20,
}

const siegeTowerSpriteConfig: TowerSpriteConfig = {
  build: {
    sheet: "/images/towers/siege/1 Upgrade/1.png",
    frameWidth: 70,
    frameHeight: 130,
    frameCount: 4,
  },
  upgrade: [
    { sheet: "/images/towers/siege/1 Upgrade/2.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/siege/1 Upgrade/3.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/siege/1 Upgrade/4.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/siege/1 Upgrade/5.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/siege/1 Upgrade/6.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/siege/1 Upgrade/7.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
  ],
  idle: [
    { sheet: "/images/towers/siege/2 Idle/1.png", frameWidth: 70, frameHeight: 130, frameCount: 1 },
    { sheet: "/images/towers/siege/2 Idle/2.png", frameWidth: 70, frameHeight: 130, frameCount: 1 },
    { sheet: "/images/towers/siege/2 Idle/3.png", frameWidth: 70, frameHeight: 130, frameCount: 1 },
    { sheet: "/images/towers/siege/2 Idle/4.png", frameWidth: 70, frameHeight: 130, frameCount: 1 },
    { sheet: "/images/towers/siege/2 Idle/5.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/siege/2 Idle/6.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/siege/2 Idle/7.png", frameWidth: 70, frameHeight: 130, frameCount: 6 },
  ],
  menuIcon: "/images/towers/siege/icon.png",
  scale: 0.65,
  offsetY: -20,
}

const barracksTowerSpriteConfig: TowerSpriteConfig = {
  build: {
    sheet: "/images/towers/barracks/1 Upgrade/1.png",
    frameWidth: 70,
    frameHeight: 130,
    frameCount: 4,
  },
  upgrade: [
    { sheet: "/images/towers/barracks/1 Upgrade/2.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/barracks/1 Upgrade/3.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/barracks/1 Upgrade/4.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/barracks/1 Upgrade/5.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/barracks/1 Upgrade/6.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
    { sheet: "/images/towers/barracks/1 Upgrade/7.png", frameWidth: 70, frameHeight: 130, frameCount: 4 },
  ],
  idle: [
    { sheet: "/images/towers/barracks/2 Idle/1.png", frameWidth: 70, frameHeight: 130, frameCount: 1 },
    { sheet: "/images/towers/barracks/2 Idle/2.png", frameWidth: 70, frameHeight: 130, frameCount: 1 },
    { sheet: "/images/towers/barracks/2 Idle/3.png", frameWidth: 70, frameHeight: 130, frameCount: 1 },
    { sheet: "/images/towers/barracks/2 Idle/4.png", frameWidth: 70, frameHeight: 130, frameCount: 1 },
    { sheet: "/images/towers/barracks/2 Idle/5.png", frameWidth: 70, frameHeight: 130, frameCount: 1 },
    { sheet: "/images/towers/barracks/2 Idle/6.png", frameWidth: 70, frameHeight: 130, frameCount: 1 },
    { sheet: "/images/towers/barracks/2 Idle/7.png", frameWidth: 70, frameHeight: 130, frameCount: 1 },
  ],
  menuIcon: "/images/towers/barracks/2 Idle/1.png",
  scale: 0.65,
  offsetY: -20,
}

const towerSpriteConfigs: Record<string, TowerSpriteConfig> = {
  archer: archerTowerSpriteConfig,
  mage: mageTowerSpriteConfig,
  siege: siegeTowerSpriteConfig,
  barracks: barracksTowerSpriteConfig,
}

// ============================================
// SHRINE TOWER SPRITE CONFIGURATION
// ============================================

const shrineImages: Record<string, HTMLImageElement> = {}

const loadShrineImage = (path: string): HTMLImageElement => {
  if (!shrineImages[path]) {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.src = path
    shrineImages[path] = img
  }
  return shrineImages[path]
}

/**
 * Draw the floating shrine object above the base
 */
const drawShrineObject = (ctx: CanvasRenderingContext2D, _tower: Tower): void => {
  const img = loadShrineImage("/images/towers/shrine/shrine_1_object.png")
  if (!img.complete || img.naturalWidth === 0) return

  // Calculate floating offset using sine wave
  const now = gameNow()
  const floatCycle = 2000 // 2 second cycle
  const floatAmplitude = 3 // 3px up/down
  const floatOffset = Math.sin((now / floatCycle) * Math.PI * 2) * floatAmplitude

  // Scale 32x32 to ~21x21 (0.66 scale)
  const scale = 0.66
  const destWidth = 32 * scale
  const destHeight = 32 * scale

  // Position above base
  const baseOffsetY = -10
  const y = baseOffsetY + floatOffset

  ctx.imageSmoothingEnabled = false
  ctx.drawImage(
    img,
    0, 0, 32, 32,
    -destWidth / 2, y - destHeight / 2,
    destWidth, destHeight
  )
}

/**
 * Draw shrine tower (base + floating object)
 */
const drawShrineTower = (ctx: CanvasRenderingContext2D, tower: Tower): void => {
  // Draw base (48x48 scaled to 32x32)
  const baseImg = loadShrineImage("/images/towers/shrine/shrine_base.png")
  if (baseImg.complete && baseImg.naturalWidth > 0) {
    const scale = 32 / 48 // 0.667
    const destSize = 48 * scale // 32px

    ctx.imageSmoothingEnabled = false
    ctx.drawImage(
      baseImg,
      0, 0, 48, 48,
      -destSize / 2, -destSize / 2,
      destSize, destSize
    )
  }

  // Draw floating object on top
  drawShrineObject(ctx, tower)
}

// ============================================
// ARCHER UNIT SPRITE CONFIGURATION
// ============================================

const archerUnitImages: Record<string, HTMLImageElement> = {}
const arrowImages: Record<number, HTMLImageElement> = {}

interface ArcherAnimConfig {
  frameCount: number
  duration: number // ms per animation cycle
}

const archerUnitConfig = {
  frameWidth: 48,
  frameHeight: 48,
  idle: { frameCount: 4, duration: 800 } as ArcherAnimConfig,
  preattack: { frameCount: 1, duration: 150 } as ArcherAnimConfig,
  attack: { frameCount: 6, duration: 400 } as ArcherAnimConfig,
  scale: 0.7,
  baseOffsetY: -22, // Base position for level 1 tower (moved down 30px from -52)
  offsetYPerLevel: -5, // Move up 5px per level upgrade
}

/**
 * Load archer unit sprite image (cached)
 */
const loadArcherUnitImage = (sheetPath: string): HTMLImageElement => {
  if (!archerUnitImages[sheetPath]) {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.src = sheetPath
    archerUnitImages[sheetPath] = img
  }
  return archerUnitImages[sheetPath]
}

/**
 * Load arrow sprite image by number (1-27)
 */
const loadArrowImage = (num: number): HTMLImageElement => {
  if (!arrowImages[num]) {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.src = `/images/towers/archer/3 Units/Arrow/${num}.png`
    arrowImages[num] = img
  }
  return arrowImages[num]
}

/**
 * Get arrow image number and flip flags based on angle
 * Arrow images: 1=North, 7=NE, 13=East (covers 0-90° in 12 steps)
 */
function getArrowImageForAngle(angle: number): { num: number; flipX: boolean; flipY: boolean } {
  // Normalize angle to 0-2π (angle is from atan2, so -π to π)
  let normalized = angle + Math.PI / 2 // Adjust so 0 = North
  normalized = ((normalized % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI)

  // Each arrow image covers ~7.5° (90° / 12 images = 7.5°)
  // Images 1-13 cover North (0°) to East (90°)
  const stepAngle = Math.PI / 24 // 7.5° in radians

  if (normalized <= Math.PI / 2) {
    // Quadrant 1: N to E (0-90°) - use images 1-13 directly
    const num = Math.round(normalized / stepAngle) + 1
    return { num: Math.min(13, Math.max(1, num)), flipX: false, flipY: false }
  } else if (normalized <= Math.PI) {
    // Quadrant 2: E to S (90-180°) - flip Y from images 13-1
    const subAngle = normalized - Math.PI / 2
    const num = 13 - Math.round(subAngle / stepAngle)
    return { num: Math.min(13, Math.max(1, num)), flipX: false, flipY: true }
  } else if (normalized <= 3 * Math.PI / 2) {
    // Quadrant 3: S to W (180-270°) - flip X and Y from images 1-13
    const subAngle = normalized - Math.PI
    const num = Math.round(subAngle / stepAngle) + 1
    return { num: Math.min(13, Math.max(1, num)), flipX: true, flipY: true }
  } else {
    // Quadrant 4: W to N (270-360°) - flip X from images 13-1
    const subAngle = normalized - 3 * Math.PI / 2
    const num = 13 - Math.round(subAngle / stepAngle)
    return { num: Math.min(13, Math.max(1, num)), flipX: true, flipY: false }
  }
}

// ============================================
// MAGE UNIT SPRITE CONFIGURATION
// ============================================

const mageUnitImages: Record<string, HTMLImageElement> = {}
let mageProjectileImage: HTMLImageElement | null = null

interface MageAnimConfig {
  frameCount: number
  duration: number // ms per animation cycle
}

const mageUnitConfig = {
  frameWidth: 48,
  frameHeight: 48,
  idle: { frameCount: 4, duration: 800 } as MageAnimConfig,
  attack: { frameCount: 6, duration: 500 } as MageAnimConfig,
  // Per-level config: [scale, offsetY] - levels 1-2 use folder 1, 3-4 use folder 2, 5-7 use folder 3
  levelConfig: [
    { folder: 1, scale: 0.7, offsetY: -17 },   // Level 1
    { folder: 1, scale: 0.7, offsetY: -22 },   // Level 2
    { folder: 2, scale: 0.75, offsetY: -27 },  // Level 3
    { folder: 2, scale: 0.8, offsetY: -32 },   // Level 4
    { folder: 3, scale: 0.85, offsetY: -37 },  // Level 5
    { folder: 3, scale: 0.85, offsetY: -42 },  // Level 6
    { folder: 3, scale: 0.85, offsetY: -47 },  // Level 7
  ],
}

/**
 * Load mage unit sprite image (cached)
 */
const loadMageUnitImage = (sheetPath: string): HTMLImageElement => {
  if (!mageUnitImages[sheetPath]) {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.src = sheetPath
    mageUnitImages[sheetPath] = img
  }
  return mageUnitImages[sheetPath]
}

/**
 * Load mage projectile sprite image (cached)
 */
const loadMageProjectileImage = (): HTMLImageElement => {
  if (!mageProjectileImage) {
    mageProjectileImage = new Image()
    mageProjectileImage.crossOrigin = "anonymous"
    mageProjectileImage.src = "/images/towers/mage/3 Units/Projectiles/3.png"
  }
  return mageProjectileImage
}

// ============================================
// SIEGE UNIT SPRITE CONFIGURATION
// ============================================

const siegeUnitImages: Record<string, HTMLImageElement> = {}
let siegeProjectileImage: HTMLImageElement | null = null

import type { SiegeFacingDirection } from "@/types/tower.types"
import { gameNow } from "@/utils/game-clock"

interface SiegeAnimConfig {
  frameCount: number
  duration: number // ms per animation cycle
}

const siegeUnitConfig = {
  frameWidth: 72,
  frameHeight: 72,
  idle: { frameCount: 8, duration: 1200 } as SiegeAnimConfig,
  attack: { frameCount: 8, duration: 600 } as SiegeAnimConfig,
  scale: 0.6,
  baseOffsetY: -25,
  offsetYPerLevel: -4,
}

/**
 * Load siege unit sprite image (cached)
 */
const loadSiegeUnitImage = (sheetPath: string): HTMLImageElement => {
  if (!siegeUnitImages[sheetPath]) {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.src = sheetPath
    siegeUnitImages[sheetPath] = img
  }
  return siegeUnitImages[sheetPath]
}

/**
 * Load siege projectile sprite image (cached)
 */
const loadSiegeProjectileImage = (): HTMLImageElement => {
  if (!siegeProjectileImage) {
    siegeProjectileImage = new Image()
    siegeProjectileImage.crossOrigin = "anonymous"
    siegeProjectileImage.src = "/images/towers/siege/3 Units/Projectiles/3.png"
  }
  return siegeProjectileImage
}

/**
 * Get sprite file and flip flag for siege unit direction
 * Uses 5 sprite files with horizontal flipping for 8 directions
 */
function getSiegeSpriteForDirection(direction: SiegeFacingDirection): { sprite: string; flipX: boolean } {
  switch (direction) {
    case "down": return { sprite: "D", flipX: false }
    case "down-left": return { sprite: "DS", flipX: false }
    case "down-right": return { sprite: "DS", flipX: true }
    case "left": return { sprite: "S", flipX: false }
    case "right": return { sprite: "S", flipX: true }
    case "up": return { sprite: "U", flipX: false }
    case "up-left": return { sprite: "US", flipX: false }
    case "up-right": return { sprite: "US", flipX: true }
  }
}

/**
 * Get tower sprite config by type
 */
export const getTowerSpriteConfig = (towerType: string): TowerSpriteConfig | undefined => {
  return towerSpriteConfigs[towerType]
}

/**
 * Load tower sprite image (cached)
 */
const loadTowerSpriteImage = (sheetPath: string): HTMLImageElement => {
  if (!towerSpriteImages[sheetPath]) {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.src = sheetPath
    towerSpriteImages[sheetPath] = img
  }
  return towerSpriteImages[sheetPath]
}

/**
 * Preload all tower sprite sheets into the cache
 */
export async function preloadTowerSprites(): Promise<void> {
  const allSheets: string[] = []

  for (const config of Object.values(towerSpriteConfigs)) {
    allSheets.push(config.build.sheet)
    config.upgrade.forEach(u => allSheets.push(u.sheet))
    config.idle.forEach(i => allSheets.push(i.sheet))
    allSheets.push(config.menuIcon)
  }

  const promises = allSheets.map(sheet => {
    return new Promise<void>((resolve) => {
      const img = loadTowerSpriteImage(sheet)
      if (img.complete && img.naturalWidth > 0) {
        resolve()
      } else {
        img.onload = () => resolve()
        img.onerror = () => resolve()
      }
    })
  })

  await Promise.all(promises)
}

/**
 * Preload all summon sprite sheets (fox, boar, turret) into the cache
 * Call this during game initialization to prevent animation glitches
 */
export async function preloadSummonSprites(): Promise<void> {
  const summonSheets: string[] = [
    // Fox sprites
    "/images/summons/Fox_Idle_with_shadow.png",
    "/images/summons/Fox_walk_with_shadow.png",
    "/images/summons/Fox_Run_with_shadow.png",
    "/images/summons/Fox_Hurt_with_shadow.png",
    "/images/summons/Fox_Death_with_shadow.png",
    // Boar sprites
    "/images/summons/Boar_Idle_with_shadow.png",
    "/images/summons/Boar_Walk_with_shadow.png",
    "/images/summons/Boar_Run_with_shadow.png",
    "/images/summons/Boar_Attack_with_shadow.png",
    "/images/summons/Boar_Hurt_with_shadow.png",
    "/images/summons/Boar_Death_with_shadow.png",
    // Turret sprites
    "/images/summons/Turret_Idle.png",
    "/images/summons/Turret_Attack.png",
    "/images/summons/Turret_Hurt.png",
    "/images/summons/Turret_Bullet.png",
  ]

  const promises = summonSheets.map(sheet => {
    return new Promise<void>((resolve) => {
      const img = loadSummonSpriteImage(sheet)
      if (img.complete && img.naturalWidth > 0) {
        resolve()
      } else {
        img.onload = () => resolve()
        img.onerror = () => resolve() // Don't block on errors
      }
    })
  })

  await Promise.all(promises)
}

// All images that should be preloaded for smooth gameplay
const ALL_GAME_IMAGES = [
  "/images/hero1.png",
  "/images/hero2.png",
  "/images/hero3.png",
  "/images/musketeer-tower.png",
  "/images/mage-tower.png",
  "/images/droppod.png",
  "/images/droppod-landed.png",
  "/images/bottom-bar-bg.png",
  "/images/bottom-bar-shop-bg.png",
  "/images/icon-coins.png",
  "/images/icon-lives.png",
  "/images/icon-waves.png",
  "/images/damage-hero.png",
  "/images/armor-hero.png",
  "/images/heroes/portraits/portrait-ranger.png",
  "/images/heroes/portraits/portrait-siegesmith.png",
  "/images/heroes/portraits/portrait-paladin.png",
  // Tileset images
  TILESET_SHEETS.ground,
  TILESET_SHEETS.treesRocks,
  TILESET_SHEETS.waterCoasts,
  TILESET_SHEETS.details,
  TILESET_SHEETS.waterShimmer1,
  TILESET_SHEETS.waterShimmer2,
]

let assetsPreloaded = false

/**
 * Preload all game images to prevent stuttering during gameplay
 * Call this before starting the game
 */
export async function preloadAllAssets(): Promise<void> {
  if (assetsPreloaded) return

  const promises = ALL_GAME_IMAGES.map((src) => {
    return new Promise<void>((resolve) => {
      const img = new Image()
      img.crossOrigin = "anonymous"
      img.onload = () => resolve()
      img.onerror = () => resolve() // Don't fail if an image is missing
      img.src = src
    })
  })

  await Promise.all(promises)
  assetsPreloaded = true
}

/**
 * Check if assets have been preloaded
 */
export function areAssetsPreloaded(): boolean {
  return assetsPreloaded
}

const loadHeroImage = (heroType: string): HTMLImageElement => {
  if (!heroImages[heroType]) {
    const img = new Image()
    img.crossOrigin = "anonymous"
    if (heroType === "knight") {
      img.src = "/images/hero1.png"
    } else if (heroType === "ranger") {
      img.src = "/images/hero2.png"
    } else if (heroType === "siegesmith") {
      img.src = "/images/hero3.png"
    } else {
      img.src = "/images/hero1.png" // fallback
    }
    heroImages[heroType] = img
  }
  return heroImages[heroType]
}

const loadEnemyImage = (imagePath: string): HTMLImageElement => {
  if (!enemyImages[imagePath]) {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.src = imagePath
    enemyImages[imagePath] = img
  }
  return enemyImages[imagePath]
}

const loadTowerImage = (towerType: string): HTMLImageElement => {
  if (!towerImages[towerType]) {
    const img = new Image()
    img.crossOrigin = "anonymous"
    if (towerType === "archer") {
      img.src = "/images/musketeer-tower.png"
    } else if (towerType === "mage") {
      img.src = "/images/mage-tower.png"
    } else {
      img.src = ""
    }
    towerImages[towerType] = img
  }
  return towerImages[towerType]
}

/**
 * Load enemy sprite image (cached)
 */
const loadEnemySpriteImage = (sheetPath: string): HTMLImageElement => {
  if (!enemySpriteImages[sheetPath]) {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.src = sheetPath
    enemySpriteImages[sheetPath] = img
  }
  return enemySpriteImages[sheetPath]
}

/**
 * Load summon sprite image (cached)
 */
const loadSummonSpriteImage = (sheetPath: string): HTMLImageElement => {
  if (!summonSpriteImages[sheetPath]) {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.src = sheetPath
    summonSpriteImages[sheetPath] = img
  }
  return summonSpriteImages[sheetPath]
}

// Summon sprite configurations
interface SummonSpriteAnimation {
  sheet: string
  frameWidth: number
  frameHeight: number
  frameCount: number
  rowCount: number
}

interface SummonSpriteConfig {
  idle: SummonSpriteAnimation
  walk: SummonSpriteAnimation
  run?: SummonSpriteAnimation
  attack?: SummonSpriteAnimation
  hurt: SummonSpriteAnimation
  death: SummonSpriteAnimation
  scale: number
  offsetY: number
}

const foxSpriteConfig: SummonSpriteConfig = {
  idle: { sheet: "/images/summons/Fox_Idle_with_shadow.png", frameWidth: 32, frameHeight: 32, frameCount: 4, rowCount: 4 },
  walk: { sheet: "/images/summons/Fox_walk_with_shadow.png", frameWidth: 32, frameHeight: 32, frameCount: 6, rowCount: 4 },
  run: { sheet: "/images/summons/Fox_Run_with_shadow.png", frameWidth: 32, frameHeight: 32, frameCount: 6, rowCount: 4 },
  hurt: { sheet: "/images/summons/Fox_Hurt_with_shadow.png", frameWidth: 32, frameHeight: 32, frameCount: 4, rowCount: 4 },
  death: { sheet: "/images/summons/Fox_Death_with_shadow.png", frameWidth: 32, frameHeight: 32, frameCount: 6, rowCount: 4 },
  scale: 0.7,  // 50% of previous 1.4
  offsetY: 3,  // Adjusted for smaller size
}

const boarSpriteConfig: SummonSpriteConfig = {
  idle: { sheet: "/images/summons/Boar_Idle_with_shadow.png", frameWidth: 32, frameHeight: 32, frameCount: 4, rowCount: 4 },
  walk: { sheet: "/images/summons/Boar_Walk_with_shadow.png", frameWidth: 32, frameHeight: 32, frameCount: 6, rowCount: 4 },
  run: { sheet: "/images/summons/Boar_Run_with_shadow.png", frameWidth: 32, frameHeight: 32, frameCount: 5, rowCount: 4 },
  attack: { sheet: "/images/summons/Boar_Attack_with_shadow.png", frameWidth: 32, frameHeight: 32, frameCount: 5, rowCount: 4 },
  hurt: { sheet: "/images/summons/Boar_Hurt_with_shadow.png", frameWidth: 32, frameHeight: 32, frameCount: 4, rowCount: 4 },
  death: { sheet: "/images/summons/Boar_Death_with_shadow.png", frameWidth: 32, frameHeight: 32, frameCount: 6, rowCount: 4 },
  scale: 0.85,  // 50% of previous 1.7
  offsetY: 4,   // Adjusted for smaller size
}

// Turret sprite configuration (single-row horizontal strips, no directional variants)
interface TurretSpriteAnimation {
  sheet: string
  frameWidth: number
  frameHeight: number
  frameCount: number
}

interface TurretSpriteConfig {
  idle: TurretSpriteAnimation
  attack: TurretSpriteAnimation
  hurt: TurretSpriteAnimation
  bullet: string
  scale: number
  offsetY: number
}

const turretSpriteConfig: TurretSpriteConfig = {
  idle: {
    sheet: "/images/summons/Turret_Idle.png",
    frameWidth: 96,
    frameHeight: 96,
    frameCount: 6,
  },
  attack: {
    sheet: "/images/summons/Turret_Attack.png",
    frameWidth: 96,
    frameHeight: 96,
    frameCount: 6,
  },
  hurt: {
    sheet: "/images/summons/Turret_Hurt.png",
    frameWidth: 96,
    frameHeight: 96,
    frameCount: 2,
  },
  bullet: "/images/summons/Turret_Bullet.png",
  scale: 0.55,
  offsetY: -10,
}

// Cache for turret bullet image
let turretBulletImage: HTMLImageElement | null = null

const loadTurretBulletImage = (): HTMLImageElement => {
  if (!turretBulletImage) {
    turretBulletImage = new Image()
    turretBulletImage.crossOrigin = "anonymous"
    turretBulletImage.src = turretSpriteConfig.bullet
  }
  return turretBulletImage
}

// Hero sprite configurations - all sprites face east, mirror for west
interface HeroSpriteAnimation {
  sheet: string
  frameWidth: number  // Always 128
  frameHeight: number // Always 128
  frameCount: number
}

interface HeroSpriteConfig {
  walk: HeroSpriteAnimation
  attack: HeroSpriteAnimation[] // Array for random selection
  hurt: HeroSpriteAnimation
  death: HeroSpriteAnimation
  idle?: HeroSpriteAnimation      // Looping standing idle (plays continuously while the hero is doing nothing)
  idle2?: HeroSpriteAnimation     // Alt looping idle, cycled into occasionally (Siegesmith)
  relaxedIdle?: HeroSpriteAnimation // One-shot "settle down" animation that holds its last frame (Paladin sits)
  skills: Record<string, HeroSpriteAnimation> // Skill-specific animations
  scale: number
  offsetX: number // Horizontal offset to center sprite (positive = right)
  offsetY: number
  attackAnimSpeed?: number // Lower = faster animation (default 6)
  walkOffsetX?: number // Additional X offset specifically for walking (applied in facing direction)
  stopFrame?: number // Specific frame to use when stopping (defaults to middle frame)
}

const heroSpriteImages: Record<string, HTMLImageElement> = {}

/**
 * Load hero sprite image (cached)
 */
const loadHeroSpriteImage = (sheetPath: string): HTMLImageElement => {
  if (!heroSpriteImages[sheetPath]) {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.src = sheetPath
    heroSpriteImages[sheetPath] = img
  }
  return heroSpriteImages[sheetPath]
}

const paladinSpriteConfig: HeroSpriteConfig = {
  walk: { sheet: "/images/heroes/Paladin_Walk.png", frameWidth: 128, frameHeight: 128, frameCount: 8 },
  attack: [
    { sheet: "/images/heroes/Paladin_Attack 1.png", frameWidth: 128, frameHeight: 128, frameCount: 5 },
    { sheet: "/images/heroes/Paladin_Attack 2.png", frameWidth: 128, frameHeight: 128, frameCount: 4 },
    { sheet: "/images/heroes/Paladin_Attack 3.png", frameWidth: 128, frameHeight: 128, frameCount: 4 },
  ],
  hurt: { sheet: "/images/heroes/Paladin_Hurt.png", frameWidth: 128, frameHeight: 128, frameCount: 2 },
  death: { sheet: "/images/heroes/Paladin_Dead.png", frameWidth: 128, frameHeight: 128, frameCount: 6 },
  idle: { sheet: "/images/heroes/Paladin_Idle.png", frameWidth: 128, frameHeight: 128, frameCount: 4 },
  relaxedIdle: { sheet: "/images/heroes/Paladin_Idle_2.png", frameWidth: 128, frameHeight: 128, frameCount: 5 },
  skills: {
    "vanguard-rush": { sheet: "/images/heroes/Paladin_Shield_Strike.png", frameWidth: 128, frameHeight: 128, frameCount: 4 },
    "spectral-blade": { sheet: "/images/heroes/Paladin_Invocation.png", frameWidth: 128, frameHeight: 128, frameCount: 5 },
  },
  scale: 0.75,
  offsetX: 8, // Compensate for off-center sprite in frame
  offsetY: -8,
  walkOffsetX: 10, // Additional offset for walking animation centering
}

const rangerSpriteConfig: HeroSpriteConfig = {
  walk: { sheet: "/images/heroes/Ranger_Walk.png", frameWidth: 128, frameHeight: 128, frameCount: 12 },
  attack: [
    { sheet: "/images/heroes/Ranger_Attack.png", frameWidth: 128, frameHeight: 128, frameCount: 12 },
  ],
  hurt: { sheet: "/images/heroes/Ranger_Hurt.png", frameWidth: 128, frameHeight: 128, frameCount: 3 },
  death: { sheet: "/images/heroes/Ranger_Dead.png", frameWidth: 128, frameHeight: 128, frameCount: 5 },
  idle: { sheet: "/images/heroes/Ranger_Idle.png", frameWidth: 128, frameHeight: 128, frameCount: 6 },
  skills: {},
  scale: 0.75,
  offsetX: 0,
  offsetY: -8,
  attackAnimSpeed: 3, // Twice as fast as default (6)
  stopFrame: 0, // Frame 0 has both feet firmly planted
}

const siegesmithSpriteConfig: HeroSpriteConfig = {
  walk: { sheet: "/images/heroes/Siegesmith_Walk.png", frameWidth: 128, frameHeight: 128, frameCount: 10 },
  attack: [
    { sheet: "/images/heroes/Siegesmith_Attack_1.png", frameWidth: 128, frameHeight: 128, frameCount: 6 },
    { sheet: "/images/heroes/Siegesmith_Attack_2.png", frameWidth: 128, frameHeight: 128, frameCount: 7 },
  ],
  hurt: { sheet: "/images/heroes/Siegesmith_Hurt.png", frameWidth: 128, frameHeight: 128, frameCount: 4 },
  death: { sheet: "/images/heroes/Siegesmith_Dead.png", frameWidth: 128, frameHeight: 128, frameCount: 5 },
  idle: { sheet: "/images/heroes/Siegesmith_Idle.png", frameWidth: 128, frameHeight: 128, frameCount: 6 },
  idle2: { sheet: "/images/heroes/Siegesmith_Idle_2.png", frameWidth: 128, frameHeight: 128, frameCount: 6 },
  skills: {
    "tremor-slam": { sheet: "/images/heroes/Siegesmith_Special.png", frameWidth: 128, frameHeight: 128, frameCount: 8 },
    "forge-turret": { sheet: "/images/heroes/Siegesmith_Jump.png", frameWidth: 128, frameHeight: 128, frameCount: 10 },
  },
  scale: 0.75,
  offsetX: 0,
  offsetY: -8,
}

const heroSpriteConfigs: Record<string, HeroSpriteConfig> = {
  paladin: paladinSpriteConfig,
  ranger: rangerSpriteConfig,
  siegesmith: siegesmithSpriteConfig,
}

/**
 * Preload all hero sprite sheets into the cache
 * Call this during game initialization to prevent animation glitches
 */
export async function preloadHeroSprites(): Promise<void> {
  const allSheets: string[] = []

  // Collect all sprite sheets from all hero configs
  for (const config of Object.values(heroSpriteConfigs)) {
    allSheets.push(config.walk.sheet)
    allSheets.push(config.hurt.sheet)
    allSheets.push(config.death.sheet)
    config.attack.forEach(a => allSheets.push(a.sheet))
    if (config.idle) allSheets.push(config.idle.sheet)
    if (config.idle2) allSheets.push(config.idle2.sheet)
    if (config.relaxedIdle) allSheets.push(config.relaxedIdle.sheet)
    Object.values(config.skills).forEach(s => allSheets.push(s.sheet))
  }

  // Preload each sheet into the cache
  const promises = allSheets.map(sheet => {
    return new Promise<void>((resolve) => {
      const img = loadHeroSpriteImage(sheet)
      if (img.complete && img.naturalWidth > 0) {
        resolve()
      } else {
        img.onload = () => resolve()
        img.onerror = () => resolve() // Don't block on errors
      }
    })
  })

  await Promise.all(promises)
}

/**
 * Preload all archer unit and arrow sprites into the cache
 * Call this during game initialization to prevent animation glitches
 */
export async function preloadArcherSprites(): Promise<void> {
  const archerSheets: string[] = []

  // Archer unit sprite sheets (3 directions x 3 animations)
  const directions = ["D", "S", "U"]
  const animations = ["Idle", "Preattack", "Attack"]
  for (const dir of directions) {
    for (const anim of animations) {
      archerSheets.push(`/images/towers/archer/3 Units/3/${dir}_${anim}.png`)
    }
  }

  // Preload archer unit sheets
  const unitPromises = archerSheets.map(sheet => {
    return new Promise<void>((resolve) => {
      const img = loadArcherUnitImage(sheet)
      if (img.complete && img.naturalWidth > 0) {
        resolve()
      } else {
        img.onload = () => resolve()
        img.onerror = () => resolve() // Don't block on errors
      }
    })
  })

  // Preload arrow sprites (1-27)
  const arrowPromises: Promise<void>[] = []
  for (let i = 1; i <= 27; i++) {
    arrowPromises.push(new Promise<void>((resolve) => {
      const img = loadArrowImage(i)
      if (img.complete && img.naturalWidth > 0) {
        resolve()
      } else {
        img.onload = () => resolve()
        img.onerror = () => resolve() // Don't block on errors
      }
    }))
  }

  await Promise.all([...unitPromises, ...arrowPromises])
}

/**
 * Preload all mage unit and projectile sprites into the cache
 * Call this during game initialization to prevent animation glitches
 */
export async function preloadMageSprites(): Promise<void> {
  const mageSheets: string[] = []

  // Mage unit sprite sheets for all level folders (1, 2, 3) x 3 directions x 2 animations
  const levelFolders = [1, 2, 3]
  const directions = ["D", "S", "U"]
  const animations = ["Idle", "Attack"]
  for (const folder of levelFolders) {
    for (const dir of directions) {
      for (const anim of animations) {
        mageSheets.push(`/images/towers/mage/3 Units/${folder}/${dir}_${anim}.png`)
      }
    }
  }

  // Preload mage unit sheets
  const unitPromises = mageSheets.map(sheet => {
    return new Promise<void>((resolve) => {
      const img = loadMageUnitImage(sheet)
      if (img.complete && img.naturalWidth > 0) {
        resolve()
      } else {
        img.onload = () => resolve()
        img.onerror = () => resolve() // Don't block on errors
      }
    })
  })

  // Preload mage projectile sprite
  const projectilePromise = new Promise<void>((resolve) => {
    const img = loadMageProjectileImage()
    if (img.complete && img.naturalWidth > 0) {
      resolve()
    } else {
      img.onload = () => resolve()
      img.onerror = () => resolve() // Don't block on errors
    }
  })

  await Promise.all([...unitPromises, projectilePromise])
}

/**
 * Preload all siege unit sprites and projectile
 */
export async function preloadSiegeSprites(): Promise<void> {
  const siegeSheets: string[] = []

  // Siege unit sprite sheets (5 directions x 1 animation - using same for idle and attack)
  // D = down, DS = down-side, S = side, U = up, US = up-side
  const directions = ["D", "DS", "S", "U", "US"]
  for (const dir of directions) {
    siegeSheets.push(`/images/towers/siege/3 Units/3/${dir}.png`)
  }

  // Preload siege unit sheets
  const unitPromises = siegeSheets.map(sheet => {
    return new Promise<void>((resolve) => {
      const img = loadSiegeUnitImage(sheet)
      if (img.complete && img.naturalWidth > 0) {
        resolve()
      } else {
        img.onload = () => resolve()
        img.onerror = () => resolve() // Don't block on errors
      }
    })
  })

  // Preload siege projectile sprite
  const projectilePromise = new Promise<void>((resolve) => {
    const img = loadSiegeProjectileImage()
    if (img.complete && img.naturalWidth > 0) {
      resolve()
    } else {
      img.onload = () => resolve()
      img.onerror = () => resolve() // Don't block on errors
    }
  })

  await Promise.all([...unitPromises, projectilePromise])
}

/**
 * Preload all enemy sprites into the cache
 * Call this during game initialization to prevent animation glitches
 * especially for hurt and death animations that appear suddenly
 */
export async function preloadEnemySprites(): Promise<void> {
  const allSheets: string[] = []

  // Collect all sprite sheets from all enemy configs
  for (const enemyConfig of Object.values(enemyTypes)) {
    const spriteConfig = enemyConfig.spriteConfig
    if (spriteConfig) {
      // Add all animation sheets
      if (spriteConfig.idle) allSheets.push(spriteConfig.idle.sheet)
      if (spriteConfig.walk) allSheets.push(spriteConfig.walk.sheet)
      if (spriteConfig.run) allSheets.push(spriteConfig.run.sheet)
      if (spriteConfig.attack) allSheets.push(spriteConfig.attack.sheet)
      if (spriteConfig.hurt) allSheets.push(spriteConfig.hurt.sheet)
      if (spriteConfig.death) allSheets.push(spriteConfig.death.sheet)
      if (spriteConfig.walkAttack) allSheets.push(spriteConfig.walkAttack.sheet)
      if (spriteConfig.runAttack) allSheets.push(spriteConfig.runAttack.sheet)
    }

    // Also preload variant sprites
    if (enemyConfig.variants?.elite?.spriteConfig) {
      const eliteConfig = enemyConfig.variants.elite.spriteConfig
      if (eliteConfig.idle) allSheets.push(eliteConfig.idle.sheet)
      if (eliteConfig.walk) allSheets.push(eliteConfig.walk.sheet)
      if (eliteConfig.run) allSheets.push(eliteConfig.run.sheet)
      if (eliteConfig.attack) allSheets.push(eliteConfig.attack.sheet)
      if (eliteConfig.hurt) allSheets.push(eliteConfig.hurt.sheet)
      if (eliteConfig.death) allSheets.push(eliteConfig.death.sheet)
      if (eliteConfig.walkAttack) allSheets.push(eliteConfig.walkAttack.sheet)
      if (eliteConfig.runAttack) allSheets.push(eliteConfig.runAttack.sheet)
    }
    if (enemyConfig.variants?.unique?.spriteConfig) {
      const uniqueConfig = enemyConfig.variants.unique.spriteConfig
      if (uniqueConfig.idle) allSheets.push(uniqueConfig.idle.sheet)
      if (uniqueConfig.walk) allSheets.push(uniqueConfig.walk.sheet)
      if (uniqueConfig.run) allSheets.push(uniqueConfig.run.sheet)
      if (uniqueConfig.attack) allSheets.push(uniqueConfig.attack.sheet)
      if (uniqueConfig.hurt) allSheets.push(uniqueConfig.hurt.sheet)
      if (uniqueConfig.death) allSheets.push(uniqueConfig.death.sheet)
      if (uniqueConfig.walkAttack) allSheets.push(uniqueConfig.walkAttack.sheet)
      if (uniqueConfig.runAttack) allSheets.push(uniqueConfig.runAttack.sheet)
    }
  }

  // Remove duplicates
  const uniqueSheets = [...new Set(allSheets)]

  // Preload each sheet into the cache
  const promises = uniqueSheets.map(sheet => {
    return new Promise<void>((resolve) => {
      const img = loadEnemySpriteImage(sheet)
      if (img.complete && img.naturalWidth > 0) {
        resolve()
      } else {
        img.onload = () => resolve()
        img.onerror = () => resolve() // Don't block on errors
      }
    })
  })

  await Promise.all(promises)
}

/**
 * Preload tileset images into the cache
 * Call this during game initialization to prevent black flash during level start
 */
export async function preloadTilesetImages(): Promise<void> {
  const tilesetPaths = [
    TILESET_SHEETS.ground,
    TILESET_SHEETS.treesRocks,
    TILESET_SHEETS.waterCoasts,
    TILESET_SHEETS.details,
    TILESET_SHEETS.waterShimmer1,
    TILESET_SHEETS.waterShimmer2,
  ]

  const promises = tilesetPaths.map(path => {
    return new Promise<void>((resolve) => {
      const img = loadTilesetImage(path)
      if (img.complete && img.naturalWidth > 0) {
        resolve()
      } else {
        img.onload = () => resolve()
        img.onerror = () => resolve() // Don't block on errors
      }
    })
  })

  await Promise.all(promises)
}

/**
 * Get the sprite config for a hero type
 */
function getHeroSpriteConfig(heroType: string): HeroSpriteConfig | undefined {
  return heroSpriteConfigs[heroType]
}

/**
 * Get facing direction for hero (only left/right since sprites face east)
 * Returns true if hero is facing left (needs mirroring)
 */
function isHeroFacingLeft(rotation: number): boolean {
  // Normalize rotation to -PI to PI
  const normalizedRotation = Math.atan2(Math.sin(rotation), Math.cos(rotation))
  // Facing left if rotation is between PI/2 and -PI/2 (pointing leftward)
  return normalizedRotation > Math.PI / 2 || normalizedRotation < -Math.PI / 2
}

/**
 * Get the sprite config for an enemy, considering its variant
 */
function getEnemySpriteConfig(enemy: Enemy): EnemySpriteConfig | undefined {
  const enemyConfig = enemyTypes[enemy.type]
  if (!enemyConfig) return undefined

  // Check if this variant has a specific sprite config
  if (enemy.variant === "elite" && enemyConfig.variants?.elite?.spriteConfig) {
    return enemyConfig.variants.elite.spriteConfig
  }
  if (enemy.variant === "unique" && enemyConfig.variants?.unique?.spriteConfig) {
    return enemyConfig.variants.unique.spriteConfig
  }

  // Fall back to base sprite config
  return enemyConfig.spriteConfig
}

/**
 * Get additional scale multiplier for enemy variants
 * Elite enemies are 25% bigger, Unique enemies are 100% bigger
 */
function getVariantScaleMultiplier(variant: "regular" | "elite" | "unique"): number {
  switch (variant) {
    case "elite":
      return 1.25
    case "unique":
      return 2.0
    default:
      return 1.0
  }
}

/**
 * Calculate the current animation frame based on time
 */
function calculateAnimationFrame(frameCount: number, animationSpeed: number = 8): number {
  // animationSpeed is frames to show each sprite frame (lower = faster)
  return Math.floor(gameNow() / (1000 / 60) / animationSpeed) % frameCount
}

export const drawEnemy = (ctx: CanvasRenderingContext2D, enemy: Enemy): void => {
  const enemyConfig = enemyTypes[enemy.type]
  const spriteConfig = getEnemySpriteConfig(enemy)
  const size = 48

  // If we have sprite configuration, draw animated sprite
  if (spriteConfig) {
    // Determine which animation to use based on state
    const animState: EnemyAnimationState = enemy.animationState
    const animation = spriteConfig[animState] || spriteConfig.walk

    if (!animation) {
      // Fallback to simple shape if no animation
      drawEnemyFallback(ctx, enemy, size)
      return
    }

    const img = loadEnemySpriteImage(animation.sheet)

    if (img.complete && img.naturalWidth > 0) {
      // Calculate animation frame
      const frameIndex = calculateAnimationFrame(animation.frameCount, animState === "hurt" ? 4 : 8)

      // Calculate source position on spritesheet
      // Row is based on facing direction, mapped through rowMapping if present
      // Default rowMapping is [0,1,2,3] (up=row0, left=row1, right=row2, down=row3)
      const rowMapping = spriteConfig.rowMapping || [0, 1, 2, 3]
      const row = rowMapping[enemy.facingDirection] ?? enemy.facingDirection
      const col = frameIndex

      const srcX = col * animation.frameWidth
      const srcY = row * animation.frameHeight

      // Calculate destination size with scale (base scale * variant scale)
      const baseScale = spriteConfig.scale || 1.0
      const variantScale = getVariantScaleMultiplier(enemy.variant)
      const scale = baseScale * variantScale
      const destSize = size * scale
      const offsetY = spriteConfig.offsetY || 0

      ctx.save()
      ctx.translate(enemy.position.x, enemy.position.y + offsetY)

      // Apply tint for hurt state
      if (enemy.hurtTimer > 0 || enemy.animationState === "hurt") {
        ctx.globalAlpha = 0.7 + Math.sin(gameNow() / 50) * 0.3
      }

      // Draw the enemy sprite (slow effect is rendered separately via drawSlowEffect)
      ctx.drawImage(
        img,
        srcX,
        srcY,
        animation.frameWidth,
        animation.frameHeight,
        -destSize / 2,
        -destSize / 2,
        destSize,
        destSize,
      )

      // Draw variant indicator for Elite/Unique
      if (enemy.variant === "elite") {
        // Draw gold star above elite enemies
        ctx.fillStyle = "#FFD700"
        ctx.beginPath()
        const starY = -destSize / 2 - 8
        ctx.moveTo(0, starY - 5)
        for (let i = 0; i < 5; i++) {
          const angle = (i * 4 * Math.PI) / 5 - Math.PI / 2
          const radius = i % 2 === 0 ? 5 : 2.5
          ctx.lineTo(Math.cos(angle) * radius, starY + Math.sin(angle) * radius)
        }
        ctx.closePath()
        ctx.fill()
      } else if (enemy.variant === "unique") {
        // Draw purple diamond above unique enemies
        ctx.fillStyle = "#9932CC"
        ctx.beginPath()
        const diamondY = -destSize / 2 - 8
        ctx.moveTo(0, diamondY - 6)
        ctx.lineTo(4, diamondY)
        ctx.lineTo(0, diamondY + 6)
        ctx.lineTo(-4, diamondY)
        ctx.closePath()
        ctx.fill()
      }

      ctx.restore()

      // Draw health bar first, then status effects on top
      drawHealthBar(ctx, enemy.position, enemy.health, enemy.maxHealth, size)
      const enemyStatusEffects = getActiveStatusEffects(enemy)
      drawStatusEffectBar(ctx, enemy.position, size, enemyStatusEffects, false)
    } else {
      // Image not loaded yet, draw fallback
      drawEnemyFallback(ctx, enemy, size)
    }
  } else if (enemyConfig?.image) {
    // Old image-based drawing
    const img = loadEnemyImage(enemyConfig.image)

    ctx.save()
    ctx.translate(enemy.position.x, enemy.position.y)

    if (img.complete && img.naturalWidth > 0) {
      ctx.drawImage(img, -size / 2, -size / 2, size, size)
    } else {
      drawEnemyShape(ctx, enemy)
    }

    ctx.restore()
    // Draw health bar first, then status effects on top
    drawHealthBar(ctx, enemy.position, enemy.health, enemy.maxHealth, size)
    const enemyStatusEffects2 = getActiveStatusEffects(enemy)
    drawStatusEffectBar(ctx, enemy.position, size, enemyStatusEffects2, false)
  } else {
    // No sprite config and no image - use fallback
    drawEnemyFallback(ctx, enemy, size)
  }
}

/**
 * Draw enemy fallback shape (when sprites aren't available)
 */
const drawEnemyFallback = (ctx: CanvasRenderingContext2D, enemy: Enemy, size: number): void => {
  ctx.save()
  ctx.translate(enemy.position.x, enemy.position.y)
  drawEnemyShape(ctx, enemy)
  ctx.restore()
  // Draw health bar first, then status effects on top
  drawHealthBar(ctx, enemy.position, enemy.health, enemy.maxHealth, size)
  const enemyStatusEffects3 = getActiveStatusEffects(enemy)
  drawStatusEffectBar(ctx, enemy.position, size, enemyStatusEffects3, false)
}

/**
 * Draw basic enemy shape (circle or square based on attack style)
 */
const drawEnemyShape = (ctx: CanvasRenderingContext2D, enemy: Enemy): void => {
  ctx.fillStyle = enemy.color
  ctx.beginPath()
  if (enemy.attackStyle === "melee") {
    ctx.rect(-enemy.size / 2, -enemy.size / 2, enemy.size, enemy.size)
  } else {
    ctx.arc(0, 0, enemy.size / 2, 0, Math.PI * 2)
  }
  ctx.fill()

  ctx.strokeStyle = "#000000"
  ctx.lineWidth = 2
  ctx.stroke()

  // Draw variant indicator
  if (enemy.variant === "elite") {
    ctx.fillStyle = "#FFD700"
    ctx.beginPath()
    ctx.arc(0, -enemy.size / 2 - 5, 4, 0, Math.PI * 2)
    ctx.fill()
  } else if (enemy.variant === "unique") {
    ctx.fillStyle = "#9932CC"
    ctx.beginPath()
    ctx.arc(0, -enemy.size / 2 - 5, 5, 0, Math.PI * 2)
    ctx.fill()
  }
}

/**
 * Draw the archer unit on top of an archer tower
 */
const drawArcherUnit = (ctx: CanvasRenderingContext2D, tower: Tower): void => {
  // Only draw for archer towers with archer state
  if (tower.type !== "archer" || !tower.archerState) return

  // Don't draw during building/upgrading animations
  if (tower.animationState !== "idle") return

  const { animationState, facingDirection, animationStartTime } = tower.archerState
  const now = gameNow()
  const elapsed = now - animationStartTime

  // Determine sprite sheet path based on direction and animation
  // D_ = down (south), S_ = side (west), U_ = up (north)
  // For "right" (east), we use S_ sprites and flip horizontally
  const dirPrefix = facingDirection === "down" ? "D" :
                    facingDirection === "up" ? "U" : "S"
  const animSuffix = animationState === "idle" ? "Idle" :
                     animationState === "preattack" ? "Preattack" : "Attack"

  const sheetPath = `/images/towers/archer/3 Units/3/${dirPrefix}_${animSuffix}.png`
  const img = loadArcherUnitImage(sheetPath)

  if (!img.complete || img.naturalWidth === 0) return

  // Get animation config
  const animConfig = archerUnitConfig[animationState]

  // Calculate frame index
  let frameIndex: number
  if (animationState === "idle") {
    // Idle loops continuously
    const progress = (elapsed % animConfig.duration) / animConfig.duration
    frameIndex = Math.floor(progress * animConfig.frameCount)
  } else {
    // Attack/preattack play once
    const progress = Math.min(elapsed / animConfig.duration, 1)
    frameIndex = Math.floor(progress * animConfig.frameCount)
  }
  frameIndex = Math.min(frameIndex, animConfig.frameCount - 1)

  ctx.save()

  // Flip horizontally for right-facing (east direction)
  if (facingDirection === "right") {
    ctx.scale(-1, 1)
  }

  // Calculate source rectangle
  const srcX = frameIndex * archerUnitConfig.frameWidth
  const srcY = 0

  // Calculate destination size
  const destWidth = archerUnitConfig.frameWidth * archerUnitConfig.scale
  const destHeight = archerUnitConfig.frameHeight * archerUnitConfig.scale

  // Calculate Y offset based on tower level (higher levels = tower is taller)
  let offsetY = archerUnitConfig.baseOffsetY + (tower.level - 1) * archerUnitConfig.offsetYPerLevel
  // Level 5 archer needs to be positioned 5px lower
  if (tower.level === 5) {
    offsetY += 5
  }

  ctx.imageSmoothingEnabled = false
  ctx.drawImage(
    img,
    srcX, srcY,
    archerUnitConfig.frameWidth, archerUnitConfig.frameHeight,
    -destWidth / 2, offsetY,
    destWidth, destHeight
  )

  ctx.restore()
}

/**
 * Draw the mage unit on top of a mage tower
 */
const drawMageUnit = (ctx: CanvasRenderingContext2D, tower: Tower): void => {
  // Only draw for mage towers with mage state
  if (tower.type !== "mage" || !tower.mageState) return

  // Don't draw during building/upgrading animations
  if (tower.animationState !== "idle") return

  const { animationState, facingDirection, animationStartTime } = tower.mageState
  const now = gameNow()
  const elapsed = now - animationStartTime

  // Get per-level config (clamp to valid range)
  const levelIndex = Math.min(Math.max(tower.level - 1, 0), mageUnitConfig.levelConfig.length - 1)
  const levelCfg = mageUnitConfig.levelConfig[levelIndex]

  // Determine sprite sheet path based on direction, animation, and tower level
  // D_ = down (south), S_ = side (west), U_ = up (north)
  // For "right" (east), we use S_ sprites and flip horizontally
  const dirPrefix = facingDirection === "down" ? "D" :
                    facingDirection === "up" ? "U" : "S"
  const animSuffix = animationState === "idle" ? "Idle" : "Attack"

  const sheetPath = `/images/towers/mage/3 Units/${levelCfg.folder}/${dirPrefix}_${animSuffix}.png`
  const img = loadMageUnitImage(sheetPath)

  if (!img.complete || img.naturalWidth === 0) return

  // Get animation config
  const animConfig = mageUnitConfig[animationState]

  // Calculate frame index
  let frameIndex: number
  if (animationState === "idle") {
    // Idle loops continuously
    const progress = (elapsed % animConfig.duration) / animConfig.duration
    frameIndex = Math.floor(progress * animConfig.frameCount)
  } else {
    // Attack plays once
    const progress = Math.min(elapsed / animConfig.duration, 1)
    frameIndex = Math.floor(progress * animConfig.frameCount)
  }
  frameIndex = Math.min(frameIndex, animConfig.frameCount - 1)

  ctx.save()

  // Flip horizontally for right-facing (east direction)
  if (facingDirection === "right") {
    ctx.scale(-1, 1)
  }

  // Calculate source rectangle
  const srcX = frameIndex * mageUnitConfig.frameWidth
  const srcY = 0

  // Calculate destination size using per-level scale
  const destWidth = mageUnitConfig.frameWidth * levelCfg.scale
  const destHeight = mageUnitConfig.frameHeight * levelCfg.scale

  // Use per-level Y offset
  const offsetY = levelCfg.offsetY

  ctx.imageSmoothingEnabled = false
  ctx.drawImage(
    img,
    srcX, srcY,
    mageUnitConfig.frameWidth, mageUnitConfig.frameHeight,
    -destWidth / 2, offsetY,
    destWidth, destHeight
  )

  ctx.restore()
}

/**
 * Draw siege catapult unit on tower
 */
const drawSiegeUnit = (ctx: CanvasRenderingContext2D, tower: Tower): void => {
  // Only draw for siege towers with siege state
  if (tower.type !== "siege" || !tower.siegeState) return

  // Don't draw during building/upgrading animations
  if (tower.animationState !== "idle") return

  const { animationState, facingDirection, animationStartTime } = tower.siegeState
  const now = gameNow()
  const elapsed = now - animationStartTime

  // Get sprite file and flip flag for direction
  const { sprite, flipX } = getSiegeSpriteForDirection(facingDirection)

  // Siege uses a single sprite sheet per direction (idle and attack are same sheet, different frame ranges)
  const sheetPath = `/images/towers/siege/3 Units/3/${sprite}.png`
  const img = loadSiegeUnitImage(sheetPath)

  if (!img.complete || img.naturalWidth === 0) return

  // Get animation config
  const animConfig = siegeUnitConfig[animationState]

  // Calculate frame index
  let frameIndex: number
  if (animationState === "idle") {
    // Idle loops continuously
    const progress = (elapsed % animConfig.duration) / animConfig.duration
    frameIndex = Math.floor(progress * animConfig.frameCount)
  } else {
    // Attack plays once
    const progress = Math.min(elapsed / animConfig.duration, 1)
    frameIndex = Math.floor(progress * animConfig.frameCount)
  }
  frameIndex = Math.min(frameIndex, animConfig.frameCount - 1)

  ctx.save()

  // Flip horizontally if needed
  if (flipX) {
    ctx.scale(-1, 1)
  }

  // Calculate source rectangle
  const srcX = frameIndex * siegeUnitConfig.frameWidth
  const srcY = 0

  // Calculate destination size
  const destWidth = siegeUnitConfig.frameWidth * siegeUnitConfig.scale
  const destHeight = siegeUnitConfig.frameHeight * siegeUnitConfig.scale

  // Calculate Y offset based on tower level (higher levels = tower is taller)
  // Level 7 needs an extra 5px north offset for proper alignment
  const level7Offset = tower.level === 7 ? -5 : 0
  const offsetY = siegeUnitConfig.baseOffsetY + (tower.level - 1) * siegeUnitConfig.offsetYPerLevel + level7Offset

  ctx.imageSmoothingEnabled = false
  ctx.drawImage(
    img,
    srcX, srcY,
    siegeUnitConfig.frameWidth, siegeUnitConfig.frameHeight,
    -destWidth / 2, offsetY,
    destWidth, destHeight
  )

  ctx.restore()
}

export const drawTower = (ctx: CanvasRenderingContext2D, tower: Tower): void => {
  ctx.save()
  ctx.translate(tower.position.x, tower.position.y)

  // Handle shrine tower with custom rendering
  if (tower.type === "shrine") {
    drawShrineTower(ctx, tower)
    ctx.restore()
    return
  }

  const spriteConfig = towerSpriteConfigs[tower.type]

  // Use animated sprite system for towers with sprite configs
  if (spriteConfig) {
    const now = gameNow()
    const elapsed = now - tower.animationStartTime

    let animation: TowerSpriteAnimation
    let frameIndex: number

    switch (tower.animationState) {
      case "building": {
        // Building a new tower (level 1) - use build animation
        animation = spriteConfig.build
        const buildProgress = Math.min(elapsed / 1000, 1)  // 1 second duration
        frameIndex = Math.floor(buildProgress * animation.frameCount)
        frameIndex = Math.min(frameIndex, animation.frameCount - 1)
        break
      }

      case "upgrading": {
        // Upgrading tower - use upgrade animation for target level
        // tower.level is already the NEW level after upgrade
        // upgrade[0] = to level 2, upgrade[1] = to level 3, etc.
        const upgradeIndex = tower.level - 2  // level 2 -> index 0
        animation = spriteConfig.upgrade[upgradeIndex] || spriteConfig.build
        const upgradeProgress = Math.min(elapsed / 1000, 1)  // 1 second duration
        frameIndex = Math.floor(upgradeProgress * animation.frameCount)
        frameIndex = Math.min(frameIndex, animation.frameCount - 1)
        break
      }

      case "idle":
      default: {
        // Continuously looping idle animation for current level
        // idle[0] = level 1, idle[1] = level 2, etc.
        animation = spriteConfig.idle[tower.level - 1] || spriteConfig.idle[0]
        // Loop through frames: 1 second per full cycle
        const idleCycleMs = 1000
        const idleProgress = (elapsed % idleCycleMs) / idleCycleMs
        frameIndex = Math.floor(idleProgress * animation.frameCount)
        frameIndex = Math.min(frameIndex, animation.frameCount - 1)
        break
      }
    }

    const img = loadTowerSpriteImage(animation.sheet)
    if (img.complete && img.naturalWidth > 0) {
      const srcX = frameIndex * animation.frameWidth
      const srcY = 0
      const destWidth = animation.frameWidth * spriteConfig.scale
      const destHeight = animation.frameHeight * spriteConfig.scale

      ctx.imageSmoothingEnabled = false
      ctx.drawImage(
        img,
        srcX, srcY,
        animation.frameWidth, animation.frameHeight,
        -destWidth / 2, -destHeight / 2 + spriteConfig.offsetY,
        destWidth, destHeight
      )
      // No gold stars - sprite visually shows upgrade level

      // Draw unit on top of the tower (at higher z-index)
      if (tower.type === "archer") {
        drawArcherUnit(ctx, tower)
      } else if (tower.type === "mage") {
        drawMageUnit(ctx, tower)
      } else if (tower.type === "siege") {
        drawSiegeUnit(ctx, tower)
      }

      ctx.restore()
      return
    }
  }

  // Fallback rendering for towers without sprite configs (mage, premium towers, etc.)
  const is2x2Tower = tower.tileWidth === 2 && tower.tileHeight === 2
  const baseSize = is2x2Tower ? (30 + tower.level * 5) * 2 : 30 + tower.level * 5
  const imageSize = is2x2Tower ? 128 : 64

  // Try loading static image for mage tower
  if (tower.type === "mage") {
    const img = loadTowerImage(tower.type)
    if (img.complete && img.naturalWidth > 0) {
      ctx.drawImage(img, -imageSize / 2, -imageSize / 2, imageSize, imageSize)

      if (tower.level > 1) {
        ctx.fillStyle = "#FFD700"
        const starSpacing = is2x2Tower ? 20 : 10
        const starRadius = is2x2Tower ? 6 : 4
        const starOffset = is2x2Tower ? -20 : -10
        for (let i = 0; i < tower.level; i++) {
          ctx.beginPath()
          ctx.arc(starOffset + i * starSpacing, imageSize * 0.4, starRadius, 0, Math.PI * 2)
          ctx.fill()
        }
      }

      ctx.restore()
      return
    }
  }

  // Final fallback: colored circle with barrel
  ctx.fillStyle = tower.color
  ctx.beginPath()
  ctx.arc(0, 0, baseSize / 2, 0, Math.PI * 2)
  ctx.fill()
  ctx.strokeStyle = "#000000"
  ctx.lineWidth = 2
  ctx.stroke()

  ctx.rotate(tower.rotation)
  const barrelWidth = is2x2Tower ? 8 : 4
  const barrelWidthTop = is2x2Tower ? 10 : 5
  ctx.fillStyle = "#555555"
  ctx.fillRect(-barrelWidth / 2, -baseSize * 0.8, barrelWidth, baseSize * 0.8)
  ctx.fillStyle = "#888888"
  ctx.fillRect(-barrelWidthTop / 2, -baseSize * 0.9, barrelWidthTop, 3)

  if (tower.level > 1) {
    ctx.fillStyle = "#FFD700"
    const starSpacing = is2x2Tower ? 16 : 8
    const starRadius = is2x2Tower ? 6 : 3
    const starOffset = is2x2Tower ? -16 : -8
    for (let i = 0; i < tower.level; i++) {
      ctx.beginPath()
      ctx.arc(starOffset + i * starSpacing, baseSize * 0.5, starRadius, 0, Math.PI * 2)
      ctx.fill()
    }
  }

  ctx.restore()
}

export const drawHero = (ctx: CanvasRenderingContext2D, hero: Hero, isSelected = false, activeItemEffects: ActiveItemEffect[] = []): void => {
  ctx.save()
  ctx.translate(hero.position.x, hero.position.y)

  const size = 36 // Reduced from 48 (25% smaller)
  const spriteConfig = getHeroSpriteConfig(hero.type)

  // Draw buff effect BEFORE the hero (so it appears behind)
  const hasEmpowerArmorBuff = hero.activeBuffs?.some((buff) => buff.skillId === "empower-armor")
  if (hasEmpowerArmorBuff) {
    drawEmpowerArmorEffect(ctx, size)
  }

  // Draw shield effect if forcefield is active (Rift Blade only)
  const hasShield = hero.maxForcefield > 0 && hero.forcefield > 0
  if (hasShield) {
    const shieldIntensity = hasEmpowerArmorBuff ? 2 : 1 // 2x when buff active
    drawShieldEffect(ctx, size, shieldIntensity)
  }

  // Draw absorb shield effect if active
  const hasAbsorbShield = activeItemEffects.some((effect) => effect.effectType === "absorb" && effect.value > 0)
  if (hasAbsorbShield) {
    drawAbsorbShieldEffect(ctx, size)
  }

  // Use sprite-based animation if config exists
  if (spriteConfig) {
    // Determine which animation to use based on state
    let animation: HeroSpriteAnimation | undefined
    let frameCount: number
    let animSpeed = 8 // Default animation speed

    // Select animation based on hero's animation state
    switch (hero.animationState) {
      case "death":
        animation = spriteConfig.death
        animSpeed = 10
        break
      case "hurt":
        animation = spriteConfig.hurt
        animSpeed = 6
        break
      case "skill":
        // Use skill-specific animation if available
        if (hero.activeSkillAnimation) {
          // For tremor-slam, use jump animation during "pre" phase, then special animation for "main" phase
          if (hero.activeSkillAnimation === "tremor-slam" && hero.skillAnimationPhase === "pre") {
            // Use the jump animation (same as forge-turret) for wind-up
            animation = spriteConfig.skills["forge-turret"]
          } else if (spriteConfig.skills[hero.activeSkillAnimation]) {
            animation = spriteConfig.skills[hero.activeSkillAnimation]
          }
          animSpeed = 6
        }
        break
      case "attack": {
        // Use random attack variant
        const attackVariant = Math.min(hero.randomAttackVariant, spriteConfig.attack.length - 1)
        animation = spriteConfig.attack[attackVariant]
        animSpeed = spriteConfig.attackAnimSpeed ?? 6
        break
        }
      case "walk":
        animation = spriteConfig.walk
        animSpeed = 6
        break
      case "relaxed-idle":
        // One-shot settle animation (Paladin sitting down), reversed when interrupted
        if (spriteConfig.relaxedIdle) {
          animation = spriteConfig.relaxedIdle
          animSpeed = 10
        } else if (spriteConfig.idle) {
          animation = spriteConfig.idle
          animSpeed = 10
        }
        break
      case "idle":
      default:
        // Standing idle loops its own sprite sheet; heroes without one fall back to
        // a static walk frame
        if (hero.selectedIdleVariant === 1 && spriteConfig.idle2) {
          animation = spriteConfig.idle2
          animSpeed = 10
        } else if (spriteConfig.idle) {
          animation = spriteConfig.idle
          animSpeed = 10
        } else {
          animation = spriteConfig.walk
          animSpeed = 999 // Very slow to appear static
        }
        break
    }

    if (animation) {
      frameCount = animation.frameCount
      const img = loadHeroSpriteImage(animation.sheet)

      if (img.complete && img.naturalWidth > 0) {
        // Calculate frame based on animation timing
        const elapsed = gameNow() - hero.animationStartTime
        let frameIndex: number

        if (hero.animationState === "idle") {
          if (spriteConfig.idle) {
            // Loop the standing idle sheet continuously
            frameIndex = Math.floor(elapsed / (1000 / 60) / animSpeed) % frameCount
          } else {
            // No idle sheet - hold stopFrame if defined, otherwise the middle walk frame
            frameIndex = spriteConfig.stopFrame !== undefined ? spriteConfig.stopFrame : Math.floor(frameCount / 2)
          }
        } else if (hero.animationState === "death") {
          // Death animation plays once and holds on last frame
          frameIndex = Math.min(Math.floor(elapsed / (1000 / 60) / animSpeed), frameCount - 1)
        } else if (hero.animationState === "hurt") {
          // Hurt animation plays once then returns to previous state
          frameIndex = Math.min(Math.floor(elapsed / (1000 / 60) / animSpeed), frameCount - 1)
        } else if (hero.animationState === "relaxed-idle") {
          // Plays once, then holds the last frame (Paladin stays seated)
          frameIndex = Math.min(Math.floor(elapsed / (1000 / 60) / animSpeed), frameCount - 1)
        } else if (hero.animationState === "skill") {
          // Skill animations play once
          frameIndex = Math.min(Math.floor(elapsed / (1000 / 60) / animSpeed), frameCount - 1)
        } else {
          // Looping animations
          frameIndex = Math.floor(elapsed / (1000 / 60) / animSpeed) % frameCount
        }

        const { frameWidth, frameHeight } = animation
        const sourceX = frameIndex * frameWidth
        const sourceY = 0 // Hero sprites are single row

        // Scale and position
        const destSize = frameWidth * spriteConfig.scale
        const facingLeft = isHeroFacingLeft(hero.rotation)

        // When mirrored (facing left), the coordinate system is flipped,
        // so we need to negate the offsetX to keep the sprite centered
        let effectiveOffsetX = facingLeft ? -spriteConfig.offsetX : spriteConfig.offsetX

        // Apply additional walking offset (moves sprite in the direction hero is facing)
        if (hero.animationState === "walk" && spriteConfig.walkOffsetX) {
          effectiveOffsetX += facingLeft ? -spriteConfig.walkOffsetX : spriteConfig.walkOffsetX
          // Paladin walking sprites need extra offset when facing west to center in bounding box
          if (hero.type === "paladin" && facingLeft) {
            effectiveOffsetX -= 35
          }
        }

        ctx.save()

        // Mirror sprite if facing left
        if (facingLeft) {
          ctx.scale(-1, 1)
        }

        // Add white glow effect around hero
        // When selected: 2x stronger glow with smooth grow/shrink animation
        if (isSelected) {
          const time = gameNow() / 400 // Animation cycle
          const pulse = Math.sin(time) * 0.5 + 0.5 // 0 to 1 range
          const glowScale = 1 + pulse * 0.3 // 1.0 to 1.3 scale range
          const glowOpacity = 0.6 + pulse * 0.4 // 0.6 to 1.0 opacity range

          // Base values doubled (16px and 32px instead of 8px and 16px)
          const innerRadius = Math.round(16 * glowScale)
          const outerRadius = Math.round(32 * glowScale)
          const innerOpacity = (0.8 * glowOpacity).toFixed(2)
          const outerOpacity = (0.4 * glowOpacity).toFixed(2)

          ctx.filter = `drop-shadow(0 0 ${innerRadius}px rgba(255, 255, 255, ${innerOpacity})) drop-shadow(0 0 ${outerRadius}px rgba(255, 255, 255, ${outerOpacity}))`
        } else {
          // Normal glow for unselected heroes
          ctx.filter = "drop-shadow(0 0 8px rgba(255, 255, 255, 0.4)) drop-shadow(0 0 16px rgba(255, 255, 255, 0.2))"
        }

        // Draw the sprite frame
        ctx.drawImage(
          img,
          sourceX,
          sourceY,
          frameWidth,
          frameHeight,
          -destSize / 2 + effectiveOffsetX,
          -destSize / 2 + spriteConfig.offsetY,
          destSize,
          destSize,
        )

        // Reset filter
        ctx.filter = "none"

        ctx.restore()
      } else {
        // Fallback while loading
        drawHeroFallback(ctx, hero, size)
      }
    } else {
      // No animation found, use fallback
      drawHeroFallback(ctx, hero, size)
    }
  } else {
    // No sprite config, use old static image method
    const img = loadHeroImage(hero.type)

    if (img.complete && img.naturalWidth > 0) {
      ctx.drawImage(img, -size / 2, -size / 2, size, size)
    } else {
      drawHeroFallback(ctx, hero, size)
    }
  }

  if (isSelected) {
    const cornerSize = 8
    // Use sprite-based size for selection box (96px sprite, but character is ~60px within it)
    const selectionSize = spriteConfig ? 60 : size
    const offset = selectionSize / 2 + 2
    // Offset the selection box to match where the character actually renders
    // The sprite has offsetY of -8, and the character sits in the lower portion of the frame
    const yOffset = spriteConfig ? (spriteConfig.offsetY + 12) : 0
    ctx.strokeStyle = "#ffffff"
    ctx.lineWidth = 3

    // Top-left corner
    ctx.beginPath()
    ctx.moveTo(-offset, -offset + yOffset)
    ctx.lineTo(-offset + cornerSize, -offset + yOffset)
    ctx.moveTo(-offset, -offset + yOffset)
    ctx.lineTo(-offset, -offset + cornerSize + yOffset)
    ctx.stroke()

    // Top-right corner
    ctx.beginPath()
    ctx.moveTo(offset, -offset + yOffset)
    ctx.lineTo(offset - cornerSize, -offset + yOffset)
    ctx.moveTo(offset, -offset + yOffset)
    ctx.lineTo(offset, -offset + cornerSize + yOffset)
    ctx.stroke()

    // Bottom-left corner
    ctx.beginPath()
    ctx.moveTo(-offset, offset + yOffset)
    ctx.lineTo(-offset + cornerSize, offset + yOffset)
    ctx.moveTo(-offset, offset + yOffset)
    ctx.lineTo(-offset, offset - cornerSize + yOffset)
    ctx.stroke()

    // Bottom-right corner
    ctx.beginPath()
    ctx.moveTo(offset, offset + yOffset)
    ctx.lineTo(offset - cornerSize, offset + yOffset)
    ctx.moveTo(offset, offset + yOffset)
    ctx.lineTo(offset, offset - cornerSize + yOffset)
    ctx.stroke()
  }

  ctx.restore()

  // Draw forcefield bar above health bar (for Rift Blade only)
  drawHeroForcefieldBar(ctx, hero.position, hero.forcefield, hero.maxForcefield, size)
  drawHeroHealthBar(ctx, hero.position, hero.health, hero.maxHealth, size)
  drawHeroExperienceBar(ctx, hero.position, hero.experience, hero.experienceToNextLevel, hero.level, size)

  // Draw status effects after health bar so they appear on top
  const heroStatusEffects = getActiveStatusEffects(hero)
  const hasForcefield = hero.maxForcefield > 0 && hero.forcefield > 0
  drawStatusEffectBar(ctx, hero.position, size, heroStatusEffects, hasForcefield)
}

/**
 * Draw fallback hero shape when sprites aren't loaded
 */
const drawHeroFallback = (ctx: CanvasRenderingContext2D, hero: Hero, size: number): void => {
  ctx.fillStyle = hero.color
  ctx.beginPath()
  ctx.moveTo(0, -size / 2)
  ctx.lineTo(-size / 3, size / 2)
  ctx.lineTo(size / 3, size / 2)
  ctx.closePath()
  ctx.fill()

  ctx.strokeStyle = "#000000"
  ctx.lineWidth = 2
  ctx.stroke()
}

/**
 * Draw the Empower Armor buff visual effect - subtle deep blue aura around hero
 * Variation on shield effect: circular, deeper blue spectrum, more subtle
 */
const drawEmpowerArmorEffect = (ctx: CanvasRenderingContext2D, heroSize: number): void => {
  const pulse = Math.sin(gameNow() / 350) * 0.25 + 0.75
  const rotation = gameNow() / 1200 // Slow rotation

  ctx.save()

  // Subtle shadow glow - deep blue
  ctx.shadowColor = "rgba(80, 140, 220, 0.3)"
  ctx.shadowBlur = 8

  // Outer glow ring - subtle, deep blue
  ctx.strokeStyle = `rgba(80, 140, 220, ${0.3 * pulse})`
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.arc(0, 0, heroSize * 0.65, 0, Math.PI * 2)
  ctx.stroke()

  // Reset shadow for inner elements
  ctx.shadowBlur = 0

  // Inner glow - subtle deep blue gradient
  const gradient = ctx.createRadialGradient(0, 0, heroSize * 0.2, 0, 0, heroSize * 0.7)
  gradient.addColorStop(0, `rgba(80, 140, 220, ${0.12 * pulse})`)
  gradient.addColorStop(0.5, `rgba(100, 160, 240, ${0.08 * pulse})`)
  gradient.addColorStop(1, "rgba(120, 180, 255, 0)")
  ctx.fillStyle = gradient
  ctx.fillRect(-heroSize, -heroSize, heroSize * 2, heroSize * 2)

  // Fewer floating/orbiting particles - deep blue
  ctx.fillStyle = `rgba(100, 160, 240, ${0.5 * pulse})`
  for (let i = 0; i < 5; i++) {
    const angle = rotation + (i / 5) * Math.PI * 2
    const dist = heroSize * 0.55 + Math.sin(gameNow() / 500 + i) * 4
    const particleX = Math.cos(angle) * dist
    const particleY = Math.sin(angle) * dist
    const particleSize = 2 + Math.sin(gameNow() / 400 + i * 0.5) * 0.8

    ctx.beginPath()
    ctx.arc(particleX, particleY, particleSize, 0, Math.PI * 2)
    ctx.fill()
  }

  // Subtle rising sparkle particles - brighter blue accent
  ctx.fillStyle = `rgba(140, 190, 255, ${0.45 * pulse})`
  for (let i = 0; i < 3; i++) {
    const phase = (gameNow() / 900 + i * 0.33) % 1
    const sparkleX = (Math.sin(i * 2.1) * heroSize * 0.35)
    const sparkleY = -heroSize * 0.45 * phase + heroSize * 0.2
    const sparkleSize = (1 - phase) * 1.8 + 1

    ctx.beginPath()
    ctx.arc(sparkleX, sparkleY, sparkleSize, 0, Math.PI * 2)
    ctx.fill()
  }

  ctx.restore()
}

/**
 * Draw shield particle effect for Rift Blade forcefield
 * White/cyan hexagonal shield with rotating fragments
 */
const drawShieldEffect = (
  ctx: CanvasRenderingContext2D,
  heroSize: number,
  intensity: number = 1,
): void => {
  const pulse = Math.sin(gameNow() / 350) * 0.25 + 0.75
  const rotation = gameNow() / 1200

  // Scale particle count and size by intensity
  const particleCount = Math.floor(6 * intensity)
  const glowRadius = heroSize * 0.7 * intensity
  const fragmentRadius = heroSize * 0.6 * intensity

  ctx.save()

  // Outer hexagonal shield ring - white/cyan pulsing
  ctx.strokeStyle = `rgba(200, 240, 255, ${0.7 * pulse * intensity})`
  ctx.lineWidth = 3 * intensity
  ctx.beginPath()
  for (let i = 0; i < 6; i++) {
    const angle = rotation + (i / 6) * Math.PI * 2
    const x = Math.cos(angle) * glowRadius
    const y = Math.sin(angle) * glowRadius
    if (i === 0) ctx.moveTo(x, y)
    else ctx.lineTo(x, y)
  }
  ctx.closePath()
  ctx.stroke()

  // Inner shield gradient - white to cyan radial
  const gradient = ctx.createRadialGradient(0, 0, heroSize * 0.2, 0, 0, heroSize * 0.8)
  gradient.addColorStop(0, `rgba(255, 255, 255, ${0.15 * intensity})`)
  gradient.addColorStop(0.5, `rgba(200, 240, 255, ${0.08 * intensity})`)
  gradient.addColorStop(1, `rgba(150, 220, 255, 0)`)
  ctx.fillStyle = gradient
  ctx.fillRect(-heroSize, -heroSize, heroSize * 2, heroSize * 2)

  // Orbiting shield fragments (4-6 particles based on intensity)
  for (let i = 0; i < particleCount; i++) {
    const angle = -rotation * 0.8 + (i / particleCount) * Math.PI * 2
    const dist = fragmentRadius + Math.sin(gameNow() / 500 + i) * 8
    const x = Math.cos(angle) * dist
    const y = Math.sin(angle) * dist

    // Fragment shape - small hexagon
    ctx.save()
    ctx.translate(x, y)
    ctx.rotate(angle + rotation * 0.5)

    ctx.fillStyle = `rgba(220, 245, 255, ${0.8 * pulse * intensity})`
    ctx.beginPath()
    for (let j = 0; j < 6; j++) {
      const fragAngle = (j / 6) * Math.PI * 2
      const fragX = Math.cos(fragAngle) * (4 * intensity)
      const fragY = Math.sin(fragAngle) * (4 * intensity)
      if (j === 0) ctx.moveTo(fragX, fragY)
      else ctx.lineTo(fragX, fragY)
    }
    ctx.closePath()
    ctx.fill()
    ctx.restore()
  }

  // Shimmering edge particles (subtle sparkles)
  const sparkleCount = Math.floor(8 * intensity)
  for (let i = 0; i < sparkleCount; i++) {
    const phase = (gameNow() / 1000 + i * 0.125) % 1
    const angle = (i / sparkleCount) * Math.PI * 2
    const dist = heroSize * 0.65 + Math.sin(phase * Math.PI * 2) * 12
    const x = Math.cos(angle) * dist
    const y = Math.sin(angle) * dist
    const opacity = Math.sin(phase * Math.PI) * 0.6 * intensity

    ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`
    ctx.beginPath()
    ctx.arc(x, y, 2.5 * intensity, 0, Math.PI * 2)
    ctx.fill()
  }

  ctx.restore()
}

/**
 * Draw the Orb of Absorption shield effect - purple/magenta protective barrier
 */
const drawAbsorbShieldEffect = (ctx: CanvasRenderingContext2D, heroSize: number): void => {
  const pulse = Math.sin(gameNow() / 250) * 0.25 + 0.75
  const rotation = gameNow() / 800

  // Outer shield ring (purple/magenta)
  ctx.strokeStyle = `rgba(180, 100, 255, ${0.5 * pulse})`
  ctx.lineWidth = 4
  ctx.beginPath()
  ctx.arc(0, 0, heroSize * 0.65, 0, Math.PI * 2)
  ctx.stroke()

  // Inner glow gradient
  const gradient = ctx.createRadialGradient(0, 0, heroSize * 0.2, 0, 0, heroSize * 0.7)
  gradient.addColorStop(0, `rgba(180, 100, 255, ${0.05 * pulse})`)
  gradient.addColorStop(0.5, `rgba(180, 100, 255, ${0.12 * pulse})`)
  gradient.addColorStop(1, "rgba(180, 100, 255, 0)")
  ctx.fillStyle = gradient
  ctx.beginPath()
  ctx.arc(0, 0, heroSize * 0.7, 0, Math.PI * 2)
  ctx.fill()

  // Orbiting shield fragments
  ctx.fillStyle = `rgba(220, 150, 255, ${0.8 * pulse})`
  for (let i = 0; i < 4; i++) {
    const angle = rotation + (i / 4) * Math.PI * 2
    const dist = heroSize * 0.55
    const fragmentX = Math.cos(angle) * dist
    const fragmentY = Math.sin(angle) * dist

    ctx.beginPath()
    ctx.arc(fragmentX, fragmentY, 3, 0, Math.PI * 2)
    ctx.fill()
  }

  // Hexagon pattern overlay (shield-like appearance)
  ctx.strokeStyle = `rgba(200, 130, 255, ${0.3 * pulse})`
  ctx.lineWidth = 1
  for (let i = 0; i < 6; i++) {
    const angle1 = (i / 6) * Math.PI * 2
    const angle2 = ((i + 1) / 6) * Math.PI * 2
    const r = heroSize * 0.5
    ctx.beginPath()
    ctx.moveTo(Math.cos(angle1) * r, Math.sin(angle1) * r)
    ctx.lineTo(Math.cos(angle2) * r, Math.sin(angle2) * r)
    ctx.stroke()
  }
}

export const drawProjectile = (ctx: CanvasRenderingContext2D, projectile: Projectile): void => {
  // Special rendering for turret summon bullets
  if (projectile.source === "tower" && projectile.sourceId?.startsWith("siege-tower")) {
    const img = loadTurretBulletImage()

    if (img.complete && img.naturalWidth > 0) {
      const scale = 2.5 // Make bullet visible (11x7 → ~28x18)
      const width = 11 * scale
      const height = 7 * scale

      ctx.save()
      ctx.translate(projectile.position.x, projectile.position.y)
      // Calculate rotation from velocity
      const angle = Math.atan2(projectile.velocity.y, projectile.velocity.x)
      ctx.rotate(angle)
      ctx.imageSmoothingEnabled = false
      ctx.drawImage(img, -width / 2, -height / 2, width, height)
      ctx.restore()
    } else {
      // Fallback
      ctx.fillStyle = "#FFD700"
      ctx.beginPath()
      ctx.arc(projectile.position.x, projectile.position.y, 4, 0, Math.PI * 2)
      ctx.fill()
    }
    return
  }

  // Special rendering for archer tower arrows
  if (projectile.towerType === "archer") {
    // Calculate angle from velocity direction
    const angle = Math.atan2(projectile.velocity.y, projectile.velocity.x)

    // Get the appropriate arrow image and flip flags
    const { num, flipX, flipY } = getArrowImageForAngle(angle)
    const img = loadArrowImage(num)

    if (img.complete && img.naturalWidth > 0) {
      ctx.save()
      ctx.translate(projectile.position.x, projectile.position.y)

      // Apply flips
      const scaleX = flipX ? -1 : 1
      const scaleY = flipY ? -1 : 1
      ctx.scale(scaleX, scaleY)

      // Draw the arrow sprite (centered, scaled up slightly)
      const scale = 1.5
      const drawWidth = img.naturalWidth * scale
      const drawHeight = img.naturalHeight * scale
      ctx.imageSmoothingEnabled = false
      ctx.drawImage(img, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight)

      ctx.restore()
      return
    }
    // Fallback to circle if image not loaded
  }

  // Special rendering for mage tower projectiles - pulsating purple orb
  if (projectile.towerType === "mage") {
    const img = loadMageProjectileImage()

    ctx.save()
    ctx.translate(projectile.position.x, projectile.position.y)

    // Pulsating purple glow effect
    const time = gameNow()
    const pulse = 0.5 + 0.5 * Math.sin(time * 0.01) // Pulsate between 0.5 and 1.0
    const glowRadius = 8 + pulse * 4 // 8-12px glow radius

    // Draw purple glow layers
    const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, glowRadius)
    gradient.addColorStop(0, `rgba(147, 112, 219, ${0.8 * pulse})`) // Purple center
    gradient.addColorStop(0.5, `rgba(128, 0, 255, ${0.4 * pulse})`) // Deeper purple
    gradient.addColorStop(1, "rgba(128, 0, 255, 0)") // Fade out

    ctx.fillStyle = gradient
    ctx.beginPath()
    ctx.arc(0, 0, glowRadius, 0, Math.PI * 2)
    ctx.fill()

    // Draw the sprite on top (scaled up)
    if (img.complete && img.naturalWidth > 0) {
      const scale = 2.5
      const drawSize = 5 * scale
      ctx.imageSmoothingEnabled = false
      ctx.drawImage(img, -drawSize / 2, -drawSize / 2, drawSize, drawSize)
    } else {
      // Fallback: draw a small white core
      ctx.fillStyle = "rgba(255, 255, 255, 0.9)"
      ctx.beginPath()
      ctx.arc(0, 0, 4, 0, Math.PI * 2)
      ctx.fill()
    }

    ctx.restore()
    return
  }

  // Special rendering for siege tower projectiles - boulder with smoke trail
  if (projectile.towerType === "siege") {
    ctx.save()
    ctx.translate(projectile.position.x, projectile.position.y)

    // Draw smoke trail particles (if projectile has trail data)
    if (projectile.trailParticles && projectile.trailParticles.length > 0) {
      for (const particle of projectile.trailParticles) {
        const alpha = particle.life / particle.maxLife
        ctx.fillStyle = `rgba(128, 128, 128, ${alpha * 0.6})`
        ctx.beginPath()
        // Particles are stored in world coords, need to offset from projectile position
        ctx.arc(particle.x - projectile.position.x, particle.y - projectile.position.y, particle.size, 0, Math.PI * 2)
        ctx.fill()
      }
    }

    // Draw the boulder projectile
    const img = loadSiegeProjectileImage()
    if (img.complete && img.naturalWidth > 0) {
      const scale = 1.2
      const drawSize = 6 * scale
      ctx.imageSmoothingEnabled = false
      ctx.drawImage(img, -drawSize / 2, -drawSize / 2, drawSize, drawSize)
    } else {
      // Fallback: draw a brown boulder
      ctx.fillStyle = "rgba(139, 90, 43, 0.9)"
      ctx.beginPath()
      ctx.arc(0, 0, 3, 0, Math.PI * 2)
      ctx.fill()
    }

    ctx.restore()
    return
  }

  // Default projectile rendering (circles)
  ctx.fillStyle = projectile.color
  ctx.beginPath()
  ctx.arc(projectile.position.x, projectile.position.y, projectile.size, 0, Math.PI * 2)
  ctx.fill()
}

export const drawSkillEffect = (ctx: CanvasRenderingContext2D, effect: ActiveSkillEffect): void => {
  // Calculate progress and fade-out (fade in last 30% of travel)
  const progress = effect.maxDistance > 0 ? effect.distanceTraveled / effect.maxDistance : 0
  const fadeAlpha = progress > 0.7 ? (1 - progress) / 0.3 : 1.0

  // Calculate rotation angle from direction
  const angle = Math.atan2(effect.direction.y, effect.direction.x)

  // Draw ghost trail of paladin silhouettes behind the effect
  const numGhosts = 5
  const ghostSpacing = 25 // pixels between each ghost
  for (let i = numGhosts; i >= 1; i--) {
    // Calculate ghost position behind the current effect position
    const ghostOffset = i * ghostSpacing
    const ghostX = effect.position.x - effect.direction.x * ghostOffset
    const ghostY = effect.position.y - effect.direction.y * ghostOffset

    // Skip if ghost would be before the start of the dash
    if (ghostOffset > effect.distanceTraveled) continue

    const ghostAlpha = 0.4 * (1 - i / (numGhosts + 1)) * fadeAlpha

    ctx.save()
    ctx.globalAlpha = ghostAlpha
    ctx.translate(ghostX, ghostY)

    // Draw a silhouette shape facing the direction of movement
    const silhouetteWidth = 24
    const silhouetteHeight = 32

    // Apply blue tint gradient
    const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, silhouetteHeight / 2)
    gradient.addColorStop(0, "rgba(100, 180, 255, 0.8)")
    gradient.addColorStop(0.5, "rgba(74, 144, 217, 0.6)")
    gradient.addColorStop(1, "rgba(50, 100, 180, 0.2)")

    ctx.fillStyle = gradient

    // Draw simplified humanoid silhouette
    ctx.beginPath()
    // Head
    ctx.arc(0, -silhouetteHeight / 2 + 6, 6, 0, Math.PI * 2)
    ctx.fill()

    // Body and shield (paladin charging)
    ctx.beginPath()
    ctx.ellipse(0, -silhouetteHeight / 4 + 4, silhouetteWidth / 3, silhouetteHeight / 3, 0, 0, Math.PI * 2)
    ctx.fill()

    // Shield arm extended forward
    const shieldAngle = angle
    ctx.save()
    ctx.rotate(shieldAngle)
    ctx.beginPath()
    ctx.ellipse(silhouetteWidth / 2.5, 0, 8, 6, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()

    ctx.restore()
  }

  ctx.save()
  ctx.globalAlpha = fadeAlpha
  ctx.translate(effect.position.x, effect.position.y)
  ctx.rotate(angle)

  // Draw the Vanguard Rush effect - a ghostly blade/energy wave
  const effectLength = 40
  const effectWidth = 16

  // Outer glow
  const gradient = ctx.createLinearGradient(-effectLength / 2, 0, effectLength / 2, 0)
  gradient.addColorStop(0, "rgba(74, 144, 217, 0)")
  gradient.addColorStop(0.3, "rgba(74, 144, 217, 0.6)")
  gradient.addColorStop(0.5, "rgba(150, 200, 255, 0.9)")
  gradient.addColorStop(0.7, "rgba(74, 144, 217, 0.6)")
  gradient.addColorStop(1, "rgba(74, 144, 217, 0)")

  // Main blade shape
  ctx.fillStyle = gradient
  ctx.beginPath()
  ctx.moveTo(-effectLength / 2, 0)
  ctx.lineTo(-effectLength / 4, -effectWidth / 2)
  ctx.lineTo(effectLength / 2, -effectWidth / 4)
  ctx.lineTo(effectLength / 2 + 10, 0)
  ctx.lineTo(effectLength / 2, effectWidth / 4)
  ctx.lineTo(-effectLength / 4, effectWidth / 2)
  ctx.closePath()
  ctx.fill()

  // Inner bright core
  ctx.fillStyle = "rgba(200, 230, 255, 0.8)"
  ctx.beginPath()
  ctx.ellipse(0, 0, effectLength / 3, effectWidth / 4, 0, 0, Math.PI * 2)
  ctx.fill()

  // Trailing particles
  ctx.fillStyle = "rgba(74, 144, 217, 0.5)"
  for (let i = 0; i < 3; i++) {
    const particleX = -effectLength / 2 - i * 8 - Math.random() * 4
    const particleY = (Math.random() - 0.5) * effectWidth
    ctx.beginPath()
    ctx.arc(particleX, particleY, 3 - i * 0.5, 0, Math.PI * 2)
    ctx.fill()
  }

  ctx.restore()
}

export const drawHealthBar = (
  ctx: CanvasRenderingContext2D,
  position: Position,
  health: number,
  maxHealth: number,
  entitySize: number,
): void => {
  withOverlayScale(ctx, position, () => drawHealthBarInner(ctx, position, health, maxHealth, entitySize))
}

const drawHealthBarInner = (
  ctx: CanvasRenderingContext2D,
  position: Position,
  health: number,
  maxHealth: number,
  entitySize: number,
): void => {
  const barWidth = 30
  const barHeight = 4
  const yOffset = entitySize / 2 + 10

  const healthPercent = Math.max(0, health / maxHealth)
  const color = healthPercent > 0.6 ? "#44ff44" : healthPercent > 0.3 ? "#ffcc00" : "#ff4444"

  ctx.fillStyle = "#222222"
  ctx.fillRect(position.x - barWidth / 2, position.y - yOffset, barWidth, barHeight)

  ctx.fillStyle = color
  ctx.fillRect(position.x - barWidth / 2 + 1, position.y - yOffset + 1, (barWidth - 2) * healthPercent, barHeight - 2)
}

export const drawHeroForcefieldBar = (
  ctx: CanvasRenderingContext2D,
  position: Position,
  forcefield: number,
  maxForcefield: number,
  entitySize: number,
): void => {
  // Don't draw if hero has no forcefield capacity
  if (maxForcefield <= 0) return

  const barWidth = 30
  const barHeight = 3
  const yOffset = entitySize / 2 + 16 // Above health bar

  const forcefieldPercent = Math.max(0, forcefield / maxForcefield)

  // Background (black)
  ctx.fillStyle = "rgba(0, 0, 0, 0.8)"
  ctx.fillRect(position.x - barWidth / 2, position.y - yOffset, barWidth, barHeight)

  // Forcefield (light blue)
  ctx.fillStyle = "rgba(100, 200, 255, 0.9)"
  ctx.fillRect(position.x - barWidth / 2 + 1, position.y - yOffset + 1, (barWidth - 2) * forcefieldPercent, barHeight - 2)
}

export const drawHeroHealthBar = (
  ctx: CanvasRenderingContext2D,
  position: Position,
  health: number,
  maxHealth: number,
  entitySize: number,
): void => {
  const barWidth = 30
  const barHeight = 4
  const yOffset = entitySize / 2 + 10

  const healthPercent = Math.max(0, health / maxHealth)
  const color = healthPercent > 0.6 ? "#44ff44" : healthPercent > 0.3 ? "#ffcc00" : "#ff4444"

  ctx.fillStyle = "#000000"
  ctx.fillRect(position.x - barWidth / 2, position.y - yOffset, barWidth, barHeight)

  ctx.fillStyle = color
  ctx.fillRect(position.x - barWidth / 2 + 1, position.y - yOffset + 1, (barWidth - 2) * healthPercent, barHeight - 2)
}

export const drawHeroExperienceBar = (
  ctx: CanvasRenderingContext2D,
  position: Position,
  experience: number,
  experienceToNextLevel: number,
  level: number,
  entitySize: number,
): void => {
  const barWidth = 30
  const barHeight = 4
  const healthBarYOffset = entitySize / 2 + 10
  const yOffset = healthBarYOffset + barHeight + 1

  let expPercent: number
  if (level >= heroLevelConfig.maxLevel) {
    expPercent = 1
  } else {
    expPercent = Math.max(0, Math.min(1, experience / experienceToNextLevel))
  }

  ctx.fillStyle = "#000000"
  ctx.fillRect(position.x - barWidth / 2, position.y - yOffset, barWidth, barHeight)

  ctx.fillStyle = "#ffffff"
  ctx.fillRect(
    position.x - barWidth / 2 + 1,
    position.y - yOffset + 1,
    (barWidth - 2) * expPercent,
    barHeight - 2,
  )
}

/**
 * Determine which status effects are active on a unit
 * Buffs are listed first, then debuffs
 */
export const getActiveStatusEffects = (unit: {
  shrineBuffs?: { activeAuraShrineId: string | null }
  slowMultiplier?: number
}): StatusEffectIcon[] => {
  const effects: StatusEffectIcon[] = []

  // Shrine buff (buffs first)
  if (unit.shrineBuffs?.activeAuraShrineId) {
    effects.push({
      type: 'shrine-buff',
      iconPath: STATUS_EFFECT_ICONS.SHRINE_BUFF,
    })
  }

  // Slow debuff (debuffs after buffs)
  if (unit.slowMultiplier !== undefined && unit.slowMultiplier < 1.0) {
    effects.push({
      type: 'slow-debuff',
      iconPath: STATUS_EFFECT_ICONS.SLOW_DEBUFF,
    })
  }

  return effects
}

/**
 * Draw status effect icons above health bar
 * Icons are 12x12px, left-aligned with 2px gaps
 *
 * @param ctx - Canvas rendering context
 * @param position - Entity world position
 * @param entitySize - Size of entity (for positioning calculation)
 * @param statusEffects - Array of active status effect icons
 * @param hasForcefield - Whether entity has active forcefield (positions above it)
 */
export const drawStatusEffectBar = (
  ctx: CanvasRenderingContext2D,
  position: Position,
  entitySize: number,
  statusEffects: StatusEffectIcon[],
  hasForcefield: boolean = false
): void => {
  withOverlayScale(ctx, position, () =>
    drawStatusEffectBarInner(ctx, position, entitySize, statusEffects, hasForcefield))
}

const drawStatusEffectBarInner = (
  ctx: CanvasRenderingContext2D,
  position: Position,
  entitySize: number,
  statusEffects: StatusEffectIcon[],
  hasForcefield: boolean = false
): void => {
  if (statusEffects.length === 0) return

  const iconSize = 12
  const iconGap = 2
  const barWidth = 30 // Same as health bar width

  // yOffset calculation:
  // - No forcefield: health bar top at entitySize/2 + 10
  //   - Icon needs to clear: 10 + 12 (icon height) + 2 (gap) = 24
  // - With forcefield: forcefield bar top at entitySize/2 + 16
  //   - Icon needs to clear: 16 + 12 (icon height) + 2 (gap) = 30
  const yOffset = hasForcefield ? entitySize / 2 + 30 : entitySize / 2 + 24

  // Left-align icons with the health bar
  const startX = position.x - barWidth / 2

  statusEffects.forEach((effect, index) => {
    const img = loadStatusEffectIcon(effect.iconPath)
    if (img.complete && img.naturalWidth > 0) {
      const x = startX + index * (iconSize + iconGap)
      const y = position.y - yOffset
      ctx.drawImage(img, x, y, iconSize, iconSize)
    }
  })
}

export const drawPath = (ctx: CanvasRenderingContext2D, path: Position[], pathWidth: number): void => {
  if (path.length < 2) return

  ctx.strokeStyle = "#4a4032"
  ctx.lineWidth = pathWidth
  ctx.lineCap = "round"
  ctx.lineJoin = "round"

  ctx.beginPath()
  ctx.moveTo(path[0].x, path[0].y)
  for (let i = 1; i < path.length; i++) {
    ctx.lineTo(path[i].x, path[i].y)
  }
  ctx.stroke()
}

export const drawRangeIndicator = (
  ctx: CanvasRenderingContext2D,
  position: Position,
  range: number,
  color = "rgba(255, 255, 255, 0.3)",
): void => {
  ctx.strokeStyle = color
  ctx.lineWidth = 2
  ctx.setLineDash([5, 5])
  ctx.beginPath()
  ctx.arc(position.x, position.y, range, 0, Math.PI * 2)
  ctx.stroke()
  ctx.setLineDash([])
}

export const drawBase = (ctx: CanvasRenderingContext2D, position: Position, path: Position[]): void => {
  const img = loadEndMarkerImage()
  if (!img.complete) return

  // Determine if last segment is vertical or horizontal
  const lastWaypoint = path[path.length - 1]
  const secondLastWaypoint = path[path.length - 2]
  const isVertical = lastWaypoint.x === secondLastWaypoint.x

  // Original image is 58x90, scale to 25%
  const scale = 0.25
  const drawWidth = 58 * scale
  const drawHeight = 90 * scale

  if (isVertical) {
    // Path is vertical - place markers left and right
    const offsetX = 50 // Distance from center
    // Left marker
    ctx.drawImage(img, 0, 0, 58, 90, position.x - offsetX - drawWidth / 2, position.y - drawHeight / 2, drawWidth, drawHeight)
    // Right marker (flipped horizontally)
    ctx.save()
    ctx.translate(position.x + offsetX + drawWidth / 2, position.y - drawHeight / 2)
    ctx.scale(-1, 1)
    ctx.drawImage(img, 0, 0, 58, 90, 0, 0, drawWidth, drawHeight)
    ctx.restore()
  } else {
    // Path is horizontal - place markers above and below
    const offsetY = 50 // Distance from center
    // Top marker
    ctx.drawImage(img, 0, 0, 58, 90, position.x - drawWidth / 2, position.y - offsetY - drawHeight / 2, drawWidth, drawHeight)
    // Bottom marker (flipped vertically)
    ctx.save()
    ctx.translate(position.x - drawWidth / 2, position.y + offsetY + drawHeight / 2)
    ctx.scale(1, -1)
    ctx.drawImage(img, 0, 0, 58, 90, 0, 0, drawWidth, drawHeight)
    ctx.restore()
  }
}

export function drawTileHighlight(
  ctx: CanvasRenderingContext2D,
  tileX: number,
  tileY: number,
  tileWidth: number,
  tileHeight: number,
  isValid: boolean,
): void {
  const pixelPos = tileToWorld(tileX, tileY)

  ctx.fillStyle = isValid ? gameSettings.colors.placement.valid : gameSettings.colors.placement.invalid
  ctx.fillRect(pixelPos.x, pixelPos.y, tileWidth * TILE_SIZE, tileHeight * TILE_SIZE)

  ctx.strokeStyle = isValid ? "rgba(0, 255, 0, 0.6)" : "rgba(255, 0, 0, 0.6)"
  ctx.lineWidth = 2
  ctx.strokeRect(pixelPos.x, pixelPos.y, tileWidth * TILE_SIZE, tileHeight * TILE_SIZE)
}

/**
 * Draw a summoned wolf - silver/white colored wolf shape
 */
export const drawSummonedWolf = (
  ctx: CanvasRenderingContext2D,
  wolf: SummonedUnit,
  isSelected: boolean,
): void => {
  ctx.save()

  // Apply fade-in opacity
  const fadeOpacity = getSummonFadeInOpacity(wolf)
  ctx.globalAlpha = fadeOpacity

  ctx.translate(wolf.position.x, wolf.position.y)
  ctx.rotate(wolf.rotation)

  const size = wolf.size

  // Wolf body - silver/white color
  ctx.fillStyle = wolf.color
  ctx.beginPath()
  // Body ellipse
  ctx.ellipse(0, 0, size * 0.6, size * 0.35, 0, 0, Math.PI * 2)
  ctx.fill()

  // Head
  ctx.beginPath()
  ctx.ellipse(size * 0.45, 0, size * 0.25, size * 0.22, 0, 0, Math.PI * 2)
  ctx.fill()

  // Ears
  ctx.beginPath()
  ctx.moveTo(size * 0.55, -size * 0.15)
  ctx.lineTo(size * 0.7, -size * 0.35)
  ctx.lineTo(size * 0.55, -size * 0.22)
  ctx.closePath()
  ctx.fill()

  ctx.beginPath()
  ctx.moveTo(size * 0.55, size * 0.15)
  ctx.lineTo(size * 0.7, size * 0.35)
  ctx.lineTo(size * 0.55, size * 0.22)
  ctx.closePath()
  ctx.fill()

  // Snout
  ctx.beginPath()
  ctx.ellipse(size * 0.65, 0, size * 0.12, size * 0.08, 0, 0, Math.PI * 2)
  ctx.fill()

  // Tail
  ctx.beginPath()
  ctx.moveTo(-size * 0.5, 0)
  ctx.quadraticCurveTo(-size * 0.8, -size * 0.2, -size * 0.7, -size * 0.35)
  ctx.quadraticCurveTo(-size * 0.65, -size * 0.25, -size * 0.45, -size * 0.05)
  ctx.closePath()
  ctx.fill()

  // Eyes - red dots for a fierce look
  ctx.fillStyle = "#ff4444"
  ctx.beginPath()
  ctx.arc(size * 0.52, -size * 0.08, 3, 0, Math.PI * 2)
  ctx.fill()
  ctx.beginPath()
  ctx.arc(size * 0.52, size * 0.08, 3, 0, Math.PI * 2)
  ctx.fill()

  // Outline
  ctx.strokeStyle = "#888888"
  ctx.lineWidth = 1.5
  ctx.beginPath()
  ctx.ellipse(0, 0, size * 0.6, size * 0.35, 0, 0, Math.PI * 2)
  ctx.stroke()

  ctx.restore()

  // Selection indicator
  if (isSelected) {
    drawSelectionIndicator(ctx, wolf.position, size)
  }

  // Draw health bar first, then status effects on top
  drawHealthBar(ctx, wolf.position, wolf.health, wolf.maxHealth, size)
  const wolfStatusEffects = getActiveStatusEffects(wolf)
  drawStatusEffectBar(ctx, wolf.position, size, wolfStatusEffects, false)
}

/**
 * Draw a siege turret summon - sprite-based rendering
 */
export const drawSummonedTower = (
  ctx: CanvasRenderingContext2D,
  tower: SummonedUnit,
  isSelected: boolean,
): void => {
  const config = turretSpriteConfig
  const now = gameNow()

  // Apply fade-in opacity
  const fadeOpacity = getSummonFadeInOpacity(tower)

  // Determine animation state
  let animation: TurretSpriteAnimation
  if (tower.hurtEndTime && now < tower.hurtEndTime) {
    animation = config.hurt
  } else if (tower.isAttacking) {
    animation = config.attack
  } else {
    animation = config.idle
  }

  const img = loadSummonSpriteImage(animation.sheet)

  if (img.complete && img.naturalWidth > 0) {
    // Calculate animation frame
    const animDuration = animation === config.attack ? 400 : 800 // Attack faster
    const frameTime = animDuration / animation.frameCount
    const frameIndex = Math.floor((now / frameTime) % animation.frameCount)

    const srcX = frameIndex * animation.frameWidth
    const srcY = 0 // Single row

    const scale = config.scale
    const destSize = animation.frameWidth * scale

    ctx.save()
    ctx.globalAlpha = fadeOpacity
    ctx.translate(tower.position.x, tower.position.y + config.offsetY)

    // Rotate turret to face target direction
    ctx.rotate(tower.rotation)

    ctx.drawImage(
      img,
      srcX,
      srcY,
      animation.frameWidth,
      animation.frameHeight,
      -destSize / 2,
      -destSize / 2,
      destSize,
      destSize,
    )

    ctx.restore()
  } else {
    // Fallback to simple shape while loading
    ctx.save()
    ctx.globalAlpha = fadeOpacity
    ctx.translate(tower.position.x, tower.position.y)
    ctx.fillStyle = tower.color
    ctx.beginPath()
    ctx.arc(0, 0, tower.size / 2, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
  }

  // Selection indicator and health bar also use fade opacity
  ctx.save()
  ctx.globalAlpha = fadeOpacity

  // Selection indicator
  const displaySize = config.scale * 96
  if (isSelected) {
    drawSelectionIndicator(ctx, tower.position, displaySize)
  }

  // Draw health bar first, then status effects on top
  drawHealthBar(ctx, tower.position, tower.health, tower.maxHealth, displaySize)
  const towerStatusEffects = getActiveStatusEffects(tower)
  drawStatusEffectBar(ctx, tower.position, displaySize, towerStatusEffects, false)
  ctx.restore()
}

/**
 * Draw a semi-transparent turret preview (for skill targeting)
 */
export const drawTurretPreview = (
  ctx: CanvasRenderingContext2D,
  position: { x: number; y: number },
  alpha: number = 0.5,
): void => {
  const config = turretSpriteConfig
  const animation = config.idle
  const img = loadSummonSpriteImage(animation.sheet)

  if (img.complete && img.naturalWidth > 0) {
    // Calculate animation frame (looping idle animation)
    const animDuration = 800 // 800ms per cycle
    const frameTime = animDuration / animation.frameCount
    const now = gameNow()
    const frameIndex = Math.floor((now / frameTime) % animation.frameCount)

    const srcX = frameIndex * animation.frameWidth
    const srcY = 0

    const scale = config.scale
    const destSize = animation.frameWidth * scale

    ctx.save()
    ctx.globalAlpha = alpha
    ctx.translate(position.x, position.y + config.offsetY)

    ctx.drawImage(
      img,
      srcX,
      srcY,
      animation.frameWidth,
      animation.frameHeight,
      -destSize / 2,
      -destSize / 2,
      destSize,
      destSize,
    )

    ctx.restore()
  } else {
    // Fallback to simple shape while sprite loads
    ctx.save()
    ctx.globalAlpha = alpha
    ctx.translate(position.x, position.y)
    ctx.fillStyle = "#CD7F32"
    ctx.beginPath()
    ctx.arc(0, 0, 20, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
  }
}

/**
 * Get facing direction from rotation angle (0=up, 1=left, 2=right, 3=down)
 */
const getDirectionFromRotation = (rotation: number): number => {
  // Normalize angle to 0-2PI range
  const normalizedAngle = ((rotation % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2)

  // Map to direction rows (0=up, 1=left, 2=right, 3=down)
  if (normalizedAngle >= Math.PI * 1.75 || normalizedAngle < Math.PI * 0.25) {
    return 2 // Right
  } else if (normalizedAngle >= Math.PI * 0.25 && normalizedAngle < Math.PI * 0.75) {
    return 3 // Down
  } else if (normalizedAngle >= Math.PI * 0.75 && normalizedAngle < Math.PI * 1.25) {
    return 1 // Left
  } else {
    return 0 // Up
  }
}

/**
 * Draw a summoned fox - sprite-based rendering
 */
export const drawSummonedFox = (
  ctx: CanvasRenderingContext2D,
  fox: SummonedUnit,
  isSelected: boolean,
): void => {
  const spriteConfig = foxSpriteConfig
  const size = 48

  // Apply fade-in opacity
  const fadeOpacity = getSummonFadeInOpacity(fox)

  // Determine animation state
  let animation: SummonSpriteAnimation
  if (fox.isAttacking) {
    animation = spriteConfig.run || spriteConfig.walk // Fox uses run animation for attack
  } else if (fox.isMoving) {
    animation = spriteConfig.walk
  } else {
    animation = spriteConfig.idle
  }

  const img = loadSummonSpriteImage(animation.sheet)

  if (img.complete && img.naturalWidth > 0) {
    // Calculate animation frame
    const frameIndex = Math.floor(gameNow() / (1000 / 60) / 8) % animation.frameCount

    // Get direction from rotation
    const row = getDirectionFromRotation(fox.rotation)
    const col = frameIndex

    const srcX = col * animation.frameWidth
    const srcY = row * animation.frameHeight

    // Calculate destination size with scale
    const scale = spriteConfig.scale
    const destSize = size * scale
    const offsetY = spriteConfig.offsetY

    ctx.save()
    ctx.globalAlpha = fadeOpacity
    ctx.translate(fox.position.x, fox.position.y + offsetY)

    ctx.drawImage(
      img,
      srcX,
      srcY,
      animation.frameWidth,
      animation.frameHeight,
      -destSize / 2,
      -destSize / 2,
      destSize,
      destSize,
    )

    ctx.restore()
  } else {
    // Fallback to simple shape
    ctx.save()
    ctx.globalAlpha = fadeOpacity
    ctx.translate(fox.position.x, fox.position.y)
    ctx.fillStyle = fox.color
    ctx.beginPath()
    ctx.ellipse(0, 0, fox.size * 0.5, fox.size * 0.3, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
  }

  // Selection indicator and health bar with fade opacity
  ctx.save()
  ctx.globalAlpha = fadeOpacity

  // Selection indicator
  if (isSelected) {
    drawSelectionIndicator(ctx, fox.position, size * spriteConfig.scale)
  }

  // Draw health bar first, then status effects on top
  drawHealthBar(ctx, fox.position, fox.health, fox.maxHealth, size * spriteConfig.scale)
  const foxStatusEffects = getActiveStatusEffects(fox)
  drawStatusEffectBar(ctx, fox.position, size * spriteConfig.scale, foxStatusEffects, false)
  ctx.restore()
}

/**
 * Draw a summoned boar - sprite-based rendering
 */
export const drawSummonedBoar = (
  ctx: CanvasRenderingContext2D,
  boar: SummonedUnit,
  isSelected: boolean,
): void => {
  const spriteConfig = boarSpriteConfig
  const size = 48

  // Apply fade-in opacity
  const fadeOpacity = getSummonFadeInOpacity(boar)

  // Determine animation state
  let animation: SummonSpriteAnimation
  if (boar.isAttacking && spriteConfig.attack) {
    animation = spriteConfig.attack
  } else if (boar.isMoving) {
    animation = spriteConfig.walk
  } else {
    animation = spriteConfig.idle
  }

  const img = loadSummonSpriteImage(animation.sheet)

  if (img.complete && img.naturalWidth > 0) {
    // Calculate animation frame
    const frameIndex = Math.floor(gameNow() / (1000 / 60) / 8) % animation.frameCount

    // Get direction from rotation
    const row = getDirectionFromRotation(boar.rotation)
    const col = frameIndex

    const srcX = col * animation.frameWidth
    const srcY = row * animation.frameHeight

    // Calculate destination size with scale
    const scale = spriteConfig.scale
    const destSize = size * scale
    const offsetY = spriteConfig.offsetY

    ctx.save()
    ctx.globalAlpha = fadeOpacity
    ctx.translate(boar.position.x, boar.position.y + offsetY)

    ctx.drawImage(
      img,
      srcX,
      srcY,
      animation.frameWidth,
      animation.frameHeight,
      -destSize / 2,
      -destSize / 2,
      destSize,
      destSize,
    )

    ctx.restore()
  } else {
    // Fallback to simple shape
    ctx.save()
    ctx.globalAlpha = fadeOpacity
    ctx.translate(boar.position.x, boar.position.y)
    ctx.fillStyle = boar.color
    ctx.beginPath()
    ctx.ellipse(0, 0, boar.size * 0.5, boar.size * 0.4, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
  }

  // Selection indicator and health bar with fade opacity
  ctx.save()
  ctx.globalAlpha = fadeOpacity

  // Selection indicator
  if (isSelected) {
    drawSelectionIndicator(ctx, boar.position, size * spriteConfig.scale)
  }

  // Draw health bar first, then status effects on top
  drawHealthBar(ctx, boar.position, boar.health, boar.maxHealth, size * spriteConfig.scale)
  const boarStatusEffects = getActiveStatusEffects(boar)
  drawStatusEffectBar(ctx, boar.position, size * spriteConfig.scale, boarStatusEffects, false)
  ctx.restore()
}

/**
 * Draw selection indicator corners around an entity with animated grow/shrink effect
 */
const drawSelectionIndicator = (ctx: CanvasRenderingContext2D, position: Position, size: number): void => {
  withOverlayScale(ctx, position, () => drawSelectionIndicatorInner(ctx, position, size))
}

const drawSelectionIndicatorInner = (ctx: CanvasRenderingContext2D, position: Position, size: number): void => {
  const cornerSize = 8
  const baseOffset = size / 2 + 4

  // Animate the selection indicator with a smooth grow/shrink cycle
  const time = gameNow() / 500 // Cycle period of ~3 seconds
  const pulse = Math.sin(time) * 0.5 + 0.5 // 0 to 1 range
  const scaleVariation = 1 + pulse * 0.15 // 1.0 to 1.15 scale range
  const offset = baseOffset * scaleVariation

  // Opacity decreases when shrinking (pulse approaching 0)
  const opacity = 0.6 + pulse * 0.4 // 0.6 to 1.0 opacity range

  ctx.strokeStyle = `rgba(255, 255, 255, ${opacity})`
  ctx.lineWidth = 3

  // Top-left corner
  ctx.beginPath()
  ctx.moveTo(position.x - offset, position.y - offset)
  ctx.lineTo(position.x - offset + cornerSize, position.y - offset)
  ctx.moveTo(position.x - offset, position.y - offset)
  ctx.lineTo(position.x - offset, position.y - offset + cornerSize)
  ctx.stroke()

  // Top-right corner
  ctx.beginPath()
  ctx.moveTo(position.x + offset, position.y - offset)
  ctx.lineTo(position.x + offset - cornerSize, position.y - offset)
  ctx.moveTo(position.x + offset, position.y - offset)
  ctx.lineTo(position.x + offset, position.y - offset + cornerSize)
  ctx.stroke()

  // Bottom-left corner
  ctx.beginPath()
  ctx.moveTo(position.x - offset, position.y + offset)
  ctx.lineTo(position.x - offset + cornerSize, position.y + offset)
  ctx.moveTo(position.x - offset, position.y + offset)
  ctx.lineTo(position.x - offset, position.y + offset - cornerSize)
  ctx.stroke()

  // Bottom-right corner
  ctx.beginPath()
  ctx.moveTo(position.x + offset, position.y + offset)
  ctx.lineTo(position.x + offset - cornerSize, position.y + offset)
  ctx.moveTo(position.x + offset, position.y + offset)
  ctx.lineTo(position.x + offset, position.y + offset - cornerSize)
  ctx.stroke()
}

/**
 * Draw slow effect overlay on an enemy (rotating dashed circle for dizzy effect)
 */
export const drawSlowEffect = (ctx: CanvasRenderingContext2D, enemy: Enemy): void => {
  if (enemy.slowMultiplier >= 1.0) return // Not slowed

  ctx.save()
  ctx.translate(enemy.position.x, enemy.position.y)

  // Rotating dizzy effect around the enemy
  const size = enemy.size * 0.7
  const slowIntensity = 1 - enemy.slowMultiplier // 0.75 for 75% slow

  // Rotate the dashed circle based on time for dizzy effect
  const rotation = (gameNow() / 200) % (Math.PI * 2)
  ctx.rotate(rotation)

  // Frost ring (rotating dashed circle)
  ctx.strokeStyle = `rgba(100, 180, 255, ${0.4 + slowIntensity * 0.3})`
  ctx.lineWidth = 2
  ctx.setLineDash([3, 3])
  ctx.beginPath()
  ctx.arc(0, 0, size * 0.8, 0, Math.PI * 2)
  ctx.stroke()
  ctx.setLineDash([])

  ctx.restore()
}

/**
 * Draw fire ground effect from siege tower impact
 * Single centered circle that scales with the effect radius
 */
export const drawFireGroundEffect = (ctx: CanvasRenderingContext2D, effect: import("@/types/game.types").FireGroundEffect): void => {
  ctx.save()
  ctx.translate(effect.position.x, effect.position.y)

  const now = gameNow()
  const fadeProgress = effect.remainingDuration / 2000 // Fade as effect ends (0-1)

  // Draw single centered fire circle with glowing gradient
  const baseGradient = ctx.createRadialGradient(0, 0, 0, 0, 0, effect.radius)
  baseGradient.addColorStop(0, `rgba(255, 200, 50, ${0.5 * fadeProgress})`) // Bright yellow center
  baseGradient.addColorStop(0.3, `rgba(255, 100, 0, ${0.45 * fadeProgress})`) // Orange
  baseGradient.addColorStop(0.6, `rgba(200, 50, 0, ${0.35 * fadeProgress})`) // Dark orange
  baseGradient.addColorStop(1, `rgba(100, 20, 0, 0)`) // Fade to transparent
  ctx.fillStyle = baseGradient
  ctx.beginPath()
  ctx.arc(0, 0, effect.radius, 0, Math.PI * 2)
  ctx.fill()

  // Animate fire particles within the circle (scale count with radius)
  const particleCount = effect.radius > 60 ? 24 : 16
  for (let i = 0; i < particleCount; i++) {
    const angle = (i / particleCount) * Math.PI * 2 + (now / 300)
    const dist = (Math.sin(now / 100 + i * 0.5) * 0.3 + 0.4) * effect.radius * 0.7
    const x = Math.cos(angle) * dist
    const y = Math.sin(angle) * dist
    const flicker = 0.6 + Math.sin(now / 80 + i) * 0.4
    const size = 4 + Math.random() * 5

    // Fire gradient - brighter colors
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, size)
    gradient.addColorStop(0, `rgba(255, 255, 100, ${flicker * fadeProgress})`)
    gradient.addColorStop(0.4, `rgba(255, 150, 0, ${flicker * 0.8 * fadeProgress})`)
    gradient.addColorStop(1, `rgba(255, 50, 0, 0)`)

    ctx.fillStyle = gradient
    ctx.beginPath()
    ctx.arc(x, y - Math.random() * 6, size, 0, Math.PI * 2)
    ctx.fill()
  }

  // Draw rising flame wisps (scale count with radius)
  const wispCount = effect.radius > 60 ? 8 : 5
  for (let i = 0; i < wispCount; i++) {
    const wispAngle = (i / wispCount) * Math.PI * 2
    const wispDist = effect.radius * 0.35
    const wispX = Math.cos(wispAngle + now / 500) * wispDist
    const wispY = Math.sin(wispAngle + now / 500) * wispDist - (now % 500) / 50
    const wispAlpha = (1 - (now % 500) / 500) * fadeProgress

    ctx.fillStyle = `rgba(255, 200, 50, ${wispAlpha * 0.5})`
    ctx.beginPath()
    ctx.arc(wispX, wispY, 2.5, 0, Math.PI * 2)
    ctx.fill()
  }

  ctx.restore()
}

/**
 * Draw entangling roots effect on the ground
 */
export const drawEntanglingRoots = (ctx: CanvasRenderingContext2D, effect: EntanglingRootsEffect): void => {
  ctx.save()
  ctx.translate(effect.position.x, effect.position.y)

  // Calculate animation phase based on remaining time
  const pulse = Math.sin(gameNow() / 200) * 0.2 + 0.8

  // Draw AoE radius circle (faded green)
  ctx.fillStyle = `rgba(34, 139, 34, ${0.1 * pulse})`
  ctx.beginPath()
  ctx.arc(0, 0, effect.aoeRadius, 0, Math.PI * 2)
  ctx.fill()

  ctx.strokeStyle = `rgba(34, 139, 34, ${0.3 * pulse})`
  ctx.lineWidth = 2
  ctx.setLineDash([5, 5])
  ctx.beginPath()
  ctx.arc(0, 0, effect.aoeRadius, 0, Math.PI * 2)
  ctx.stroke()
  ctx.setLineDash([])

  // Draw roots emanating from center
  const rootColor = `rgba(101, 67, 33, ${0.8 * pulse})` // Brown color
  const leafColor = `rgba(34, 139, 34, ${0.9 * pulse})` // Green color

  ctx.strokeStyle = rootColor
  ctx.lineWidth = 3

  // Draw 6 main root tendrils
  for (let i = 0; i < 6; i++) {
    const angle = (i / 6) * Math.PI * 2
    const length = 20 + Math.sin(gameNow() / 300 + i) * 5

    ctx.beginPath()
    ctx.moveTo(0, 0)

    // Curved root path
    const midX = Math.cos(angle) * length * 0.5
    const midY = Math.sin(angle) * length * 0.5
    const endX = Math.cos(angle + 0.1) * length
    const endY = Math.sin(angle + 0.1) * length

    ctx.quadraticCurveTo(midX + 5, midY - 5, endX, endY)
    ctx.stroke()

    // Draw small leaves at root tips
    ctx.fillStyle = leafColor
    ctx.beginPath()
    ctx.arc(endX, endY, 4, 0, Math.PI * 2)
    ctx.fill()
  }

  // Draw center thorns
  ctx.fillStyle = `rgba(0, 100, 0, ${0.8 * pulse})`
  for (let i = 0; i < 4; i++) {
    const angle = (i / 4) * Math.PI * 2 + gameNow() / 1000
    const x = Math.cos(angle) * 8
    const y = Math.sin(angle) * 8

    ctx.beginPath()
    ctx.moveTo(x, y - 6)
    ctx.lineTo(x - 3, y + 3)
    ctx.lineTo(x + 3, y + 3)
    ctx.closePath()
    ctx.fill()
  }

  ctx.restore()
}

/**
 * Draw root effect overlay on an enemy (green vines)
 */
export const drawRootEffect = (ctx: CanvasRenderingContext2D, enemy: Enemy): void => {
  // Check if enemy has root status effect
  const hasRoot = enemy.statusEffects.some((e) => e.type === "root")
  if (!hasRoot) return

  ctx.save()
  ctx.translate(enemy.position.x, enemy.position.y)

  const size = enemy.size * 0.7
  const pulse = Math.sin(gameNow() / 200) * 0.2 + 0.8

  // Green vine effect wrapping around the enemy
  ctx.strokeStyle = `rgba(34, 139, 34, ${0.7 * pulse})`
  ctx.lineWidth = 3

  // Draw spiral vines
  for (let i = 0; i < 3; i++) {
    const offset = (i / 3) * Math.PI * 2
    ctx.beginPath()
    for (let t = 0; t <= Math.PI * 2; t += 0.2) {
      const r = size * 0.5 + Math.sin(t * 3 + offset) * 5
      const x = Math.cos(t + offset) * r
      const y = Math.sin(t + offset) * r
      if (t === 0) {
        ctx.moveTo(x, y)
      } else {
        ctx.lineTo(x, y)
      }
    }
    ctx.stroke()
  }

  // Draw thorns around the enemy
  ctx.fillStyle = `rgba(0, 100, 0, ${0.8 * pulse})`
  for (let i = 0; i < 8; i++) {
    const angle = (i / 8) * Math.PI * 2
    const dist = size * 0.6
    const x = Math.cos(angle) * dist
    const y = Math.sin(angle) * dist

    ctx.beginPath()
    ctx.moveTo(x, y - 4)
    ctx.lineTo(x - 2, y + 2)
    ctx.lineTo(x + 2, y + 2)
    ctx.closePath()
    ctx.fill()
  }

  ctx.restore()
}

/**
 * Get sprite config for a corpse based on enemy type and variant
 */
function getCorpseSpriteConfig(enemyType: string, enemyVariant: "regular" | "elite" | "unique") {
  const enemyConfig = enemyTypes[enemyType]
  if (!enemyConfig) return undefined

  // Check if this variant has a specific sprite config
  if (enemyVariant === "elite" && enemyConfig.variants?.elite?.spriteConfig) {
    return enemyConfig.variants.elite.spriteConfig
  }
  if (enemyVariant === "unique" && enemyConfig.variants?.unique?.spriteConfig) {
    return enemyConfig.variants.unique.spriteConfig
  }

  // Fall back to base sprite config
  return enemyConfig.spriteConfig
}

/**
 * Draw a corpse left behind by a dead enemy
 * Uses the final frame of the enemy's death animation, facing opposite to the killing blow
 */
export const drawCorpse = (ctx: CanvasRenderingContext2D, corpse: Corpse): void => {
  ctx.save()
  ctx.translate(corpse.position.x, corpse.position.y)

  // Calculate opacity based on remaining time (fade out in last 5 seconds)
  const elapsed = gameNow() - corpse.createdAt
  const remaining = corpse.duration - elapsed
  const fadeStart = 5000 // Start fading 5 seconds before expiration
  let opacity = 0.8

  if (remaining < fadeStart) {
    opacity = 0.8 * (remaining / fadeStart)
  }

  ctx.globalAlpha = opacity

  // Try to draw sprite-based corpse
  const spriteConfig = getCorpseSpriteConfig(corpse.enemyType, corpse.enemyVariant)

  if (spriteConfig?.death) {
    const deathAnim = spriteConfig.death
    const img = loadEnemySpriteImage(deathAnim.sheet)

    if (img.complete && img.naturalWidth > 0) {
      // Use the last frame of the death animation
      const lastFrameIndex = deathAnim.frameCount - 1
      // Apply row mapping for spritesheets with non-standard direction layout
      const rowMapping = spriteConfig.rowMapping || [0, 1, 2, 3]
      const row = rowMapping[corpse.facingDirection] ?? corpse.facingDirection
      const col = lastFrameIndex

      const srcX = col * deathAnim.frameWidth
      const srcY = row * deathAnim.frameHeight

      // Calculate destination size with scale (base scale * variant scale)
      const baseScale = spriteConfig.scale || 1.0
      const variantScale = getVariantScaleMultiplier(corpse.enemyVariant)
      const scale = baseScale * variantScale
      const size = 48 * scale
      const offsetY = spriteConfig.offsetY || 0

      ctx.drawImage(
        img,
        srcX,
        srcY,
        deathAnim.frameWidth,
        deathAnim.frameHeight,
        -size / 2,
        -size / 2 + offsetY,
        size,
        size,
      )

      ctx.restore()
      return
    }
  }

  // Fallback: Draw red corpse square if no sprite available
  const size = TILE_SIZE

  ctx.fillStyle = `rgba(139, 0, 0, 1)`
  ctx.fillRect(-size / 2, -size / 2, size, size)

  ctx.strokeStyle = `rgba(80, 0, 0, 1)`
  ctx.lineWidth = 2
  ctx.strokeRect(-size / 2, -size / 2, size, size)

  ctx.strokeStyle = `rgba(50, 0, 0, 0.7)`
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(-size / 4, -size / 4)
  ctx.lineTo(size / 4, size / 4)
  ctx.moveTo(size / 4, -size / 4)
  ctx.lineTo(-size / 4, size / 4)
  ctx.stroke()

  ctx.restore()
}

/**
 * Load a tileset image (cached)
 */
const loadTilesetImage = (sheetPath: string): HTMLImageElement => {
  if (!tilesetImages[sheetPath]) {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.src = sheetPath
    tilesetImages[sheetPath] = img
  }
  return tilesetImages[sheetPath]
}

// Per-level caches for sorted decoration arrays (decorations never change
// during a level, so sorting every frame is wasted work + GC pressure)
const sortedDecorationsCache = new WeakMap<LevelDecoration[], LevelDecoration[]>()
const sortedObjectDecorationsCache = new WeakMap<ObjectDecoration[], ObjectDecoration[]>()

// ===== STATIC TILEMAP CACHE =====
// The ground layer + decorations never change during a level, but drawing them
// tile-by-tile costs ~2500 drawImage calls per frame (each logical tile is four
// 16px subtiles). That alone caps phones well below 60fps. Instead we bake the
// whole world into one offscreen canvas and blit the visible rect each frame.

interface TilemapCacheEntry {
  canvas: HTMLCanvasElement
  /** False while some source images were still decoding - rebuild when they land. */
  complete: boolean
}

const tilemapCanvasCache = new WeakMap<LevelTilemap, TilemapCacheEntry>()

/** Every image the baked tilemap needs, so we know when the bake is final. */
const collectTilemapImages = (tilemap: LevelTilemap): HTMLImageElement[] => {
  const images = [
    loadTilesetImage(TILESET_SHEETS.ground),
    loadTilesetImage(TILESET_SHEETS.treesRocks),
    loadTilesetImage(TILESET_SHEETS.details),
  ]
  for (const obj of tilemap.objectDecorations ?? []) {
    images.push(loadObjectDecorationImage(obj.imagePath))
  }
  return images
}

const isImageReady = (img: HTMLImageElement): boolean => img.complete && img.naturalWidth > 0

/**
 * Get (building it on first use) the baked static layer for a tilemap.
 * Returns null if the ground sheet hasn't decoded yet - callers fall back to
 * per-tile drawing for those first few frames.
 */
const getStaticTilemapCanvas = (tilemap: LevelTilemap): HTMLCanvasElement | null => {
  const images = collectTilemapImages(tilemap)
  const allReady = images.every(isImageReady)

  const cached = tilemapCanvasCache.get(tilemap)
  // Reuse unless the previous bake was missing images that have since loaded
  if (cached && (cached.complete || !allReady)) return cached.canvas

  // Nothing to bake yet - the ground sheet is what everything else sits on
  if (!isImageReady(images[0])) return null

  const canvas = cached?.canvas ?? document.createElement("canvas")
  const worldWidth = tilemap.width * TILE_SIZE
  const worldHeight = tilemap.height * TILE_SIZE
  if (canvas.width !== worldWidth || canvas.height !== worldHeight) {
    canvas.width = worldWidth
    canvas.height = worldHeight
  } else {
    canvas.getContext("2d")?.clearRect(0, 0, worldWidth, worldHeight)
  }

  const bakeCtx = canvas.getContext("2d")
  if (!bakeCtx) return null

  // Bake the entire world (no viewport culling - this runs once per level)
  drawTilemapDirect(bakeCtx, tilemap, 0, 0, worldWidth, worldHeight)

  tilemapCanvasCache.set(tilemap, { canvas, complete: allReady })
  return canvas
}

/**
 * Draw the level tilemap (ground layer + decorations)
 * Blits a pre-baked world canvas; falls back to per-tile drawing while the
 * tileset images are still decoding.
 */
export const drawLevelTilemap = (
  ctx: CanvasRenderingContext2D,
  tilemap: LevelTilemap,
  viewportX: number,
  viewportY: number,
  viewportWidth: number,
  viewportHeight: number,
): void => {
  const baked = getStaticTilemapCanvas(tilemap)
  if (!baked) {
    drawTilemapDirect(ctx, tilemap, viewportX, viewportY, viewportWidth, viewportHeight)
    return
  }

  // Clamp the source rect to the baked canvas - drawImage with a source
  // rectangle that runs off the edge draws nothing at all in some browsers.
  const srcX = Math.max(0, Math.floor(viewportX))
  const srcY = Math.max(0, Math.floor(viewportY))
  const srcW = Math.min(baked.width - srcX, Math.ceil(viewportWidth + (viewportX - srcX)) + 1)
  const srcH = Math.min(baked.height - srcY, Math.ceil(viewportHeight + (viewportY - srcY)) + 1)
  if (srcW <= 0 || srcH <= 0) return

  ctx.save()
  ctx.imageSmoothingEnabled = false
  ctx.drawImage(baked, srcX, srcY, srcW, srcH, srcX, srcY, srcW, srcH)
  ctx.restore()
}

/**
 * Draw the tilemap tile-by-tile into a context.
 * Used to bake the cache, and as the fallback before the bake is possible.
 * Uses native 16px subtiles for precise autotiling.
 */
const drawTilemapDirect = (
  ctx: CanvasRenderingContext2D,
  tilemap: LevelTilemap,
  viewportX: number,
  viewportY: number,
  viewportWidth: number,
  viewportHeight: number,
): void => {
  const groundImg = loadTilesetImage(TILESET_SHEETS.ground)
  const treesRocksImg = loadTilesetImage(TILESET_SHEETS.treesRocks)
  const detailsImg = loadTilesetImage(TILESET_SHEETS.details)

  // Calculate visible logical tile range
  const startTileX = Math.max(0, Math.floor(viewportX / TILE_SIZE))
  const startTileY = Math.max(0, Math.floor(viewportY / TILE_SIZE))
  const endTileX = Math.min(tilemap.width, Math.ceil((viewportX + viewportWidth) / TILE_SIZE) + 1)
  const endTileY = Math.min(tilemap.height, Math.ceil((viewportY + viewportHeight) / TILE_SIZE) + 1)

  ctx.save()
  ctx.imageSmoothingEnabled = false // Pixel-perfect rendering

  // Draw ground layer using 16px subtiles (no scaling)
  if (groundImg.complete && groundImg.naturalWidth > 0) {
    for (let tileY = startTileY; tileY < endTileY; tileY++) {
      for (let tileX = startTileX; tileX < endTileX; tileX++) {
        const tile = tilemap.tiles[tileY]?.[tileX]
        if (!tile) continue

        // Each logical tile has 4 subtiles (2x2)
        if (tile.subtiles) {
          // Draw pre-computed subtiles at native 16px size
          for (let subY = 0; subY < 2; subY++) {
            for (let subX = 0; subX < 2; subX++) {
              const coords = tile.subtiles[subY][subX]
              const destX = tileX * TILE_SIZE + subX * SUBTILE_SIZE
              const destY = tileY * TILE_SIZE + subY * SUBTILE_SIZE

              ctx.drawImage(
                groundImg,
                coords.x * SPRITE_TILE_SIZE,
                coords.y * SPRITE_TILE_SIZE,
                SPRITE_TILE_SIZE,
                SPRITE_TILE_SIZE,
                destX,
                destY,
                SUBTILE_SIZE, // Native size - no scaling
                SUBTILE_SIZE,
              )
            }
          }
        } else {
          // Fallback: draw single sprite scaled to 32px (legacy support)
          const coords = { x: 2, y: 1 } // Default grass
          ctx.drawImage(
            groundImg,
            coords.x * SPRITE_TILE_SIZE,
            coords.y * SPRITE_TILE_SIZE,
            SPRITE_TILE_SIZE,
            SPRITE_TILE_SIZE,
            tileX * TILE_SIZE,
            tileY * TILE_SIZE,
            TILE_SIZE,
            TILE_SIZE,
          )
        }
      }
    }
  }

  // Sort decorations by Y position for proper layering (further back draws first).
  // Decorations are static per level, so cache the sorted copy instead of
  // re-sorting on every frame.
  let sortedDecorations = sortedDecorationsCache.get(tilemap.decorations)
  if (!sortedDecorations) {
    sortedDecorations = [...tilemap.decorations].sort((a, b) => {
      const defA = DECORATION_DEFS[a.type]
      const defB = DECORATION_DEFS[b.type]
      const zIndexA = defA?.zIndex || 0
      const zIndexB = defB?.zIndex || 0
      // Sort by Y first (for depth), then by zIndex
      const yA = a.tileY + (defA?.coords.height || 1)
      const yB = b.tileY + (defB?.coords.height || 1)
      if (yA !== yB) return yA - yB
      return zIndexA - zIndexB
    })
    sortedDecorationsCache.set(tilemap.decorations, sortedDecorations)
  }

  // Draw decorations
  for (const decoration of sortedDecorations) {
    drawDecoration(ctx, decoration, groundImg, treesRocksImg, detailsImg, startTileX, endTileX, startTileY, endTileY)
  }

  // Draw object decorations (individual PNG images)
  if (tilemap.objectDecorations) {
    // Sort by Y position + height for proper layering, then by zIndex (cached - static per level)
    let sortedObjects = sortedObjectDecorationsCache.get(tilemap.objectDecorations)
    if (!sortedObjects) {
      sortedObjects = [...tilemap.objectDecorations].sort((a, b) => {
        const yA = a.tileY + a.heightTiles
        const yB = b.tileY + b.heightTiles
        if (yA !== yB) return yA - yB
        return a.zIndex - b.zIndex
      })
      sortedObjectDecorationsCache.set(tilemap.objectDecorations, sortedObjects)
    }

    for (const obj of sortedObjects) {
      drawObjectDecoration(ctx, obj, startTileX, endTileX, startTileY, endTileY)
    }
  }

  ctx.restore()
}

/**
 * Draw a single object decoration (individual PNG image)
 */
const drawObjectDecoration = (
  ctx: CanvasRenderingContext2D,
  obj: ObjectDecoration,
  visStartX: number,
  visEndX: number,
  visStartY: number,
  visEndY: number,
): void => {
  // Check if object is visible in viewport
  const objEndX = obj.tileX + obj.widthTiles
  const objEndY = obj.tileY + obj.heightTiles
  if (objEndX < visStartX || obj.tileX > visEndX || objEndY < visStartY || obj.tileY > visEndY) {
    return // Not visible
  }

  const img = loadObjectDecorationImage(obj.imagePath)
  if (!img.complete || img.naturalWidth === 0) return

  // Draw at tile position, full image size
  const destX = obj.tileX * TILE_SIZE
  const destY = obj.tileY * TILE_SIZE
  const destW = obj.widthTiles * TILE_SIZE
  const destH = obj.heightTiles * TILE_SIZE

  ctx.drawImage(img, destX, destY, destW, destH)
}

/**
 * Draw a single decoration
 */
const drawDecoration = (
  ctx: CanvasRenderingContext2D,
  decoration: LevelDecoration,
  groundImg: HTMLImageElement,
  treesRocksImg: HTMLImageElement,
  detailsImg: HTMLImageElement,
  visStartX: number,
  visEndX: number,
  visStartY: number,
  visEndY: number,
): void => {
  const def = DECORATION_DEFS[decoration.type]
  if (!def) return

  const coords = def.coords
  const width = coords.width || 1
  const height = coords.height || 1

  // Check if decoration is in visible range
  const decorationEndX = decoration.tileX + width
  const decorationEndY = decoration.tileY + height
  if (
    decorationEndX < visStartX ||
    decoration.tileX > visEndX ||
    decorationEndY < visStartY ||
    decoration.tileY > visEndY
  ) {
    return // Not visible
  }

  // Select the correct image based on sheet
  let img: HTMLImageElement
  if (def.sheet === "treesRocks") {
    img = treesRocksImg
  } else if (def.sheet === "ground") {
    img = groundImg
  } else {
    img = detailsImg
  }
  if (!img.complete || img.naturalWidth === 0) return

  const srcX = coords.x * SPRITE_TILE_SIZE
  const srcY = coords.y * SPRITE_TILE_SIZE
  const srcW = width * SPRITE_TILE_SIZE
  const srcH = height * SPRITE_TILE_SIZE

  const destX = decoration.tileX * TILE_SIZE
  const destY = decoration.tileY * TILE_SIZE
  const destW = width * TILE_SIZE
  const destH = height * TILE_SIZE

  ctx.drawImage(img, srcX, srcY, srcW, srcH, destX, destY, destW, destH)
}

/**
 * Check if a tile has an impassable decoration
 */
export const isDecorationImpassable = (tilemap: LevelTilemap, tileX: number, tileY: number): boolean => {
  for (const decoration of tilemap.decorations) {
    const def = DECORATION_DEFS[decoration.type]
    if (!def || !def.impassable) continue

    const width = def.coords.width || 1
    const height = def.coords.height || 1

    // Check if tile is within decoration bounds
    if (
      tileX >= decoration.tileX &&
      tileX < decoration.tileX + width &&
      tileY >= decoration.tileY &&
      tileY < decoration.tileY + height
    ) {
      return true
    }
  }
  return false
}

/**
 * Check if a tile is buildable (no impassable decorations)
 */
export const isDecorationBuildable = (tilemap: LevelTilemap, tileX: number, tileY: number): boolean => {
  for (const decoration of tilemap.decorations) {
    const def = DECORATION_DEFS[decoration.type]
    if (!def) continue

    const width = def.coords.width || 1
    const height = def.coords.height || 1

    // Check if tile is within decoration bounds
    if (
      tileX >= decoration.tileX &&
      tileX < decoration.tileX + width &&
      tileY >= decoration.tileY &&
      tileY < decoration.tileY + height
    ) {
      return def.buildable
    }
  }
  return true // No decoration here, so buildable
}

/**
 * Draw a continuous laser beam from source to target
 * Red outer glow with white inner core
 */
export const drawLaserBeam = (
  ctx: CanvasRenderingContext2D,
  sourcePosition: Position,
  targetPosition: Position,
): void => {
  const now = gameNow()

  // Slight beam flicker/pulse for visual interest
  const flicker = 0.9 + Math.sin(now / 50) * 0.1

  // Calculate beam direction for impact particles

  // Outer red glow (widest layer)
  ctx.beginPath()
  ctx.moveTo(sourcePosition.x, sourcePosition.y)
  ctx.lineTo(targetPosition.x, targetPosition.y)
  ctx.strokeStyle = `rgba(255, 30, 30, ${0.3 * flicker})`
  ctx.lineWidth = 16
  ctx.lineCap = "round"
  ctx.stroke()

  // Middle red layer
  ctx.beginPath()
  ctx.moveTo(sourcePosition.x, sourcePosition.y)
  ctx.lineTo(targetPosition.x, targetPosition.y)
  ctx.strokeStyle = `rgba(255, 50, 50, ${0.5 * flicker})`
  ctx.lineWidth = 10
  ctx.lineCap = "round"
  ctx.stroke()

  // Bright red inner glow
  ctx.beginPath()
  ctx.moveTo(sourcePosition.x, sourcePosition.y)
  ctx.lineTo(targetPosition.x, targetPosition.y)
  ctx.strokeStyle = `rgba(255, 100, 100, ${0.8 * flicker})`
  ctx.lineWidth = 6
  ctx.lineCap = "round"
  ctx.stroke()

  // White hot core
  ctx.beginPath()
  ctx.moveTo(sourcePosition.x, sourcePosition.y)
  ctx.lineTo(targetPosition.x, targetPosition.y)
  ctx.strokeStyle = `rgba(255, 255, 255, ${0.95 * flicker})`
  ctx.lineWidth = 3
  ctx.lineCap = "round"
  ctx.stroke()

  // Draw impact effect at target position
  drawLaserImpact(ctx, targetPosition)
}

/**
 * Draw burning impact effect where laser hits the target
 * Similar to fire ground effect but smaller and more intense
 */
export const drawLaserImpact = (
  ctx: CanvasRenderingContext2D,
  position: Position,
): void => {
  ctx.save()
  ctx.translate(position.x, position.y)

  const now = gameNow()
  const impactRadius = 12

  // Glowing impact point
  const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, impactRadius)
  gradient.addColorStop(0, "rgba(255, 255, 200, 0.9)")
  gradient.addColorStop(0.3, "rgba(255, 150, 50, 0.7)")
  gradient.addColorStop(0.6, "rgba(255, 50, 30, 0.4)")
  gradient.addColorStop(1, "rgba(200, 30, 0, 0)")

  ctx.fillStyle = gradient
  ctx.beginPath()
  ctx.arc(0, 0, impactRadius, 0, Math.PI * 2)
  ctx.fill()

  // Animated fire particles around impact
  for (let i = 0; i < 8; i++) {
    const angle = (i / 8) * Math.PI * 2 + (now / 150)
    const dist = (Math.sin(now / 80 + i * 1.2) * 0.3 + 0.6) * impactRadius
    const x = Math.cos(angle) * dist
    const y = Math.sin(angle) * dist - Math.random() * 4
    const flicker = 0.5 + Math.sin(now / 60 + i * 0.7) * 0.5
    const size = 3 + Math.random() * 3

    // Fire particle gradient
    const particleGradient = ctx.createRadialGradient(x, y, 0, x, y, size)
    particleGradient.addColorStop(0, `rgba(255, 255, 150, ${flicker})`)
    particleGradient.addColorStop(0.4, `rgba(255, 100, 30, ${flicker * 0.8})`)
    particleGradient.addColorStop(1, "rgba(200, 30, 0, 0)")

    ctx.fillStyle = particleGradient
    ctx.beginPath()
    ctx.arc(x, y, size, 0, Math.PI * 2)
    ctx.fill()
  }

  // Rising sparks/embers
  for (let i = 0; i < 4; i++) {
    const sparkPhase = ((now / 200) + i * 0.25) % 1
    const sparkX = (Math.sin(i * 2.5) * 6)
    const sparkY = -sparkPhase * 15
    const sparkAlpha = (1 - sparkPhase) * 0.8
    const sparkSize = 2 * (1 - sparkPhase * 0.5)

    ctx.fillStyle = `rgba(255, 200, 100, ${sparkAlpha})`
    ctx.beginPath()
    ctx.arc(sparkX, sparkY, sparkSize, 0, Math.PI * 2)
    ctx.fill()
  }

  ctx.restore()
}

// Draw abduction beam effect for level completion animation
export const drawAbductionBeam = (
  ctx: CanvasRenderingContext2D,
  heroPosition: Position,
  beamTopY: number,
  opacity: number,
): void => {
  const now = gameNow()
  const flicker = 0.95 + Math.sin(now / 80) * 0.05
  const effectiveOpacity = opacity * flicker

  const beamCenterX = heroPosition.x
  const beamBottomY = heroPosition.y
  const beamHeight = beamBottomY - beamTopY + 100

  ctx.save()

  // Outer blue glow (widest)
  ctx.beginPath()
  ctx.moveTo(beamCenterX, beamTopY - 100)
  ctx.lineTo(beamCenterX, beamBottomY)
  ctx.strokeStyle = `rgba(0, 100, 255, ${0.15 * effectiveOpacity})`
  ctx.lineWidth = 80
  ctx.lineCap = "round"
  ctx.stroke()

  // Middle blue layer
  ctx.beginPath()
  ctx.moveTo(beamCenterX, beamTopY - 100)
  ctx.lineTo(beamCenterX, beamBottomY)
  ctx.strokeStyle = `rgba(50, 150, 255, ${0.3 * effectiveOpacity})`
  ctx.lineWidth = 50
  ctx.stroke()

  // Bright inner blue
  ctx.beginPath()
  ctx.moveTo(beamCenterX, beamTopY - 100)
  ctx.lineTo(beamCenterX, beamBottomY)
  ctx.strokeStyle = `rgba(100, 200, 255, ${0.5 * effectiveOpacity})`
  ctx.lineWidth = 30
  ctx.stroke()

  // White hot core
  ctx.beginPath()
  ctx.moveTo(beamCenterX, beamTopY - 100)
  ctx.lineTo(beamCenterX, beamBottomY)
  ctx.strokeStyle = `rgba(255, 255, 255, ${0.8 * effectiveOpacity})`
  ctx.lineWidth = 12
  ctx.stroke()

  // Bright white center line
  ctx.beginPath()
  ctx.moveTo(beamCenterX, beamTopY - 100)
  ctx.lineTo(beamCenterX, beamBottomY)
  ctx.strokeStyle = `rgba(255, 255, 255, ${0.95 * effectiveOpacity})`
  ctx.lineWidth = 4
  ctx.stroke()

  // Draw sparkles along the beam
  for (let i = 0; i < 8; i++) {
    const sparkleY = beamTopY + (beamHeight * ((now / 500 + i * 0.125) % 1))
    const sparkleSize = 3 + Math.sin(now / 100 + i) * 2
    const sparkleOpacity = 0.6 + Math.sin(now / 80 + i * 0.5) * 0.4

    ctx.beginPath()
    ctx.arc(beamCenterX, sparkleY, sparkleSize, 0, Math.PI * 2)
    ctx.fillStyle = `rgba(255, 255, 255, ${sparkleOpacity * effectiveOpacity})`
    ctx.fill()
  }

  ctx.restore()
}

// ============================================
// BARRACKS SOLDIER RENDERING
// ============================================

const soldierSpriteImages: Record<string, HTMLImageElement> = {}

// Soldier sprite config
// Frame size: 48x48, Idle: 4 frames, Walk: 6 frames, Attack: 6 frames, Death: 6 frames
interface SoldierSpriteAnimation {
  frameWidth: number
  frameHeight: number
  frameCount: number
  animDuration: number // ms per cycle
}

const SOLDIER_FRAME_SIZE = 48
const SOLDIER_FRAME_SIZE_LEVEL_7 = 96 // Level 7 (Royal Guard) uses 96x96 sprites
const SOLDIER_BASE_SCALE = 0.85 // Base scale for level 1 soldiers (slightly smaller)
const SOLDIER_SCALE_PER_LEVEL = 0.05 // 5% size increase per level
const SOLDIER_FADE_IN_DURATION = 500

// Standard soldier animations (levels 1-6 use 48x48 frames)
const soldierAnimations: Record<SoldierAnimationState, SoldierSpriteAnimation> = {
  idle: { frameWidth: 48, frameHeight: 48, frameCount: 4, animDuration: 800 },
  walk: { frameWidth: 48, frameHeight: 48, frameCount: 6, animDuration: 500 },
  attack: { frameWidth: 48, frameHeight: 48, frameCount: 6, animDuration: 400 },
  death: { frameWidth: 48, frameHeight: 48, frameCount: 6, animDuration: 600 },
}

// Level 7 (Royal Guard) uses 96x96 frames
const soldierAnimationsLevel7: Record<SoldierAnimationState, SoldierSpriteAnimation> = {
  idle: { frameWidth: 96, frameHeight: 96, frameCount: 4, animDuration: 800 },
  walk: { frameWidth: 96, frameHeight: 96, frameCount: 6, animDuration: 500 },
  attack: { frameWidth: 96, frameHeight: 96, frameCount: 6, animDuration: 400 },
  death: { frameWidth: 96, frameHeight: 96, frameCount: 6, animDuration: 600 },
}

// Helper to get animation config based on barracks level
const getSoldierAnimation = (level: number, animState: SoldierAnimationState): SoldierSpriteAnimation => {
  return level === 7 ? soldierAnimationsLevel7[animState] : soldierAnimations[animState]
}

// Get sprite path for soldier based on barracks level, animation, facing direction, and soldier index
// Sprite tier is determined by barracks level only:
// - Level 7 → tier 7 (Royal Guard sprites)
// - Level 6 → tier 6 (Champion sprites)
// - Level 5 → tier 5 (Knight sprites)
// - Level 4 → tier 4 (Man-at-Arms sprites)
// - Level 3 → tier 3 (Swordsman sprites)
// - Level 2 → tier 2 (Militia sprites)
// - Level 1 → tier 1A/1B (soldier index 0 = 1A, index 1 = 1B, index 2 = random)
// Note: Tiers 4, 6, and 7 don't have Idle sprites, so we fall back to Walk
// Note: Tiers 1A/1B don't have Attack/Death sprites - use Special for attack, Walk for death

// Cache for soldier 1A/1B random assignments (soldierIndex 2 only)
// Deterministic 1A/1B pick for reinforcement soldiers - hashing the id keeps
// the choice stable across frames without an ever-growing assignment map
const soldier1ABFromId = (soldierId: string): "1A" | "1B" => {
  let h = 0
  for (let i = 0; i < soldierId.length; i++) {
    h = (h * 31 + soldierId.charCodeAt(i)) | 0
  }
  return (h & 1) === 0 ? "1A" : "1B"
}

const getSoldierSpritePath = (
  level: number,
  animState: SoldierAnimationState,
  facing: SoldierFacingDirection,
  upgradeState?: UpgradeState,
  soldierIndex: number = 0,
  soldierId?: string,
): string => {
  // Determine sprite tier based on barracks level only (upgrades no longer affect sprites)
  let spriteTier: string = "1A"
  let useTier1AB = false

  // Level 7 barracks use tier 7 sprites (Royal Guard)
  // Level 6 barracks use tier 6 sprites (Champion)
  // Level 5 barracks use tier 5 sprites (Knight)
  // Level 4 barracks use tier 4 sprites (Man-at-Arms)
  // Level 3 barracks use tier 3 sprites (Swordsman)
  // Level 2 barracks use tier 2 sprites (Militia)
  // Level 1 barracks use tier 1A/1B sprites (Footman)
  if (level === 7) {
    spriteTier = "7"
  } else if (level === 6) {
    spriteTier = "6"
  } else if (level === 5) {
    spriteTier = "5"
  } else if (level === 4) {
    spriteTier = "4"
  } else if (level === 3) {
    spriteTier = "3"
  } else if (level === 2) {
    spriteTier = "2"
  } else {
    useTier1AB = true
  }

  // For tier 1, use 1A/1B based on soldier index
  if (useTier1AB) {
    if (soldierIndex === 0) {
      spriteTier = "1A"
    } else if (soldierIndex === 1) {
      spriteTier = "1B"
    } else {
      // Index 2 (reinforcements) - deterministic pick from the soldier id
      spriteTier = soldierId ? soldier1ABFromId(soldierId) : "1A"
    }
  }

  // Map facing to sprite prefix
  const facingPrefix: Record<SoldierFacingDirection, string> = {
    up: "U",
    down: "D",
    side: "S",
  }

  // Map animation to sprite name
  // Note: Tiers 4, 6, and 7 don't have Idle sprites, fall back to Walk
  // Note: Tiers 1A/1B don't have Attack/Death sprites - use Special for attack, Walk for death
  let effectiveAnimState = animState
  const isTier1AB = spriteTier === "1A" || spriteTier === "1B"

  if (isTier1AB) {
    // 1A/1B only have Idle, Special, Walk - no Attack or Death
    if (animState === "attack") {
      effectiveAnimState = "attack" // Will be mapped to "Special" below
    } else if (animState === "death") {
      effectiveAnimState = "walk" // Fall back to Walk for death
    }
  } else if ((spriteTier === "4" || spriteTier === "6" || spriteTier === "7") && animState === "idle") {
    effectiveAnimState = "walk"
  }

  // Animation name mapping - different for 1A/1B tiers
  let animFileName: string
  if (isTier1AB) {
    const animName1AB: Record<SoldierAnimationState, string> = {
      idle: "Idle",
      walk: "Walk",
      attack: "Special", // 1A/1B use Special instead of Attack
      death: "Walk", // 1A/1B don't have Death, use Walk
    }
    animFileName = animName1AB[effectiveAnimState]
  } else {
    const animName: Record<SoldierAnimationState, string> = {
      idle: "Idle",
      walk: "Walk",
      attack: "Attack",
      death: "Death",
    }
    animFileName = animName[effectiveAnimState]
  }

  return `/images/towers/barracks/3 Units/${spriteTier}/${facingPrefix[facing]}_${animFileName}.png`
}

const loadSoldierSpriteImage = (path: string): HTMLImageElement => {
  if (!soldierSpriteImages[path]) {
    const img = new Image()
    img.src = path
    soldierSpriteImages[path] = img
  }
  return soldierSpriteImages[path]
}

// Calculate fade-in opacity for newly spawned soldiers
const getSoldierFadeInOpacity = (soldier: BarracksSoldier): number => {
  const elapsed = gameNow() - soldier.spawnTime
  if (elapsed >= SOLDIER_FADE_IN_DURATION) return 1
  return elapsed / SOLDIER_FADE_IN_DURATION
}

/**
 * Draw a barracks soldier
 */
export const drawBarracksSoldier = (
  ctx: CanvasRenderingContext2D,
  soldier: BarracksSoldier,
  barrackLevel: number,
  isSelected: boolean = false,
  upgradeState?: UpgradeState,
): void => {
  if (soldier.isDead) return

  const now = gameNow()
  const fadeOpacity = getSoldierFadeInOpacity(soldier)

  // Calculate scale based on barracks level (level 1 = base, +10% per level)
  const levelScale = SOLDIER_BASE_SCALE + (barrackLevel - 1) * SOLDIER_SCALE_PER_LEVEL

  const animState = soldier.animationState
  const facing = soldier.facingDirection
  const animation = getSoldierAnimation(barrackLevel, animState)

  const spritePath = getSoldierSpritePath(barrackLevel, animState, facing, upgradeState, soldier.soldierIndex, soldier.id)
  const img = loadSoldierSpriteImage(spritePath)

  // Draw oval glow at the base of the soldier (below selection circle)
  // Glow intensity scales with level: level 1 = faint (0.15), level 7 = strong (0.5)
  const glowIntensity = 0.15 + (barrackLevel - 1) * 0.058 // 0.15 to ~0.5
  const glowWidth = 18 * levelScale
  const glowHeight = 8 * levelScale

  ctx.save()
  ctx.globalAlpha = fadeOpacity * glowIntensity

  // Create radial gradient for oval glow
  const gradient = ctx.createRadialGradient(
    soldier.position.x, soldier.position.y + 2, 0,
    soldier.position.x, soldier.position.y + 2, glowWidth
  )
  gradient.addColorStop(0, "rgba(255, 200, 100, 1)") // Warm amber center
  gradient.addColorStop(0.5, "rgba(255, 180, 80, 0.6)")
  gradient.addColorStop(1, "rgba(255, 160, 60, 0)")

  ctx.fillStyle = gradient
  ctx.beginPath()
  ctx.ellipse(soldier.position.x, soldier.position.y + 2, glowWidth, glowHeight, 0, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()

  if (img.complete && img.naturalWidth > 0) {
    // Calculate animation frame
    const frameTime = animation.animDuration / animation.frameCount
    const elapsed = now - soldier.animationStartTime
    let frameIndex: number

    if (animState === "death") {
      // Death animation doesn't loop - stick at last frame
      frameIndex = Math.min(animation.frameCount - 1, Math.floor(elapsed / frameTime))
    } else {
      frameIndex = Math.floor((now / frameTime) % animation.frameCount)
    }

    const srcX = frameIndex * animation.frameWidth
    const srcY = 0

    // Use appropriate frame size for level 7 (Royal Guard uses 96x96 sprites)
    const frameSize = barrackLevel === 7 ? SOLDIER_FRAME_SIZE_LEVEL_7 : SOLDIER_FRAME_SIZE
    const destSize = frameSize * levelScale

    ctx.save()
    ctx.globalAlpha = fadeOpacity

    // Flip horizontally for side-facing when rotation indicates facing left
    if (facing === "side" && Math.cos(soldier.rotation) < 0) {
      ctx.translate(soldier.position.x, soldier.position.y)
      ctx.scale(-1, 1)
      ctx.drawImage(
        img,
        srcX,
        srcY,
        animation.frameWidth,
        animation.frameHeight,
        -destSize / 2,
        -destSize / 2 - 8, // Offset up slightly
        destSize,
        destSize,
      )
    } else {
      ctx.drawImage(
        img,
        srcX,
        srcY,
        animation.frameWidth,
        animation.frameHeight,
        soldier.position.x - destSize / 2,
        soldier.position.y - destSize / 2 - 8, // Offset up slightly
        destSize,
        destSize,
      )
    }

    ctx.restore()
  } else {
    // Fallback to simple shape while sprite loads
    ctx.save()
    ctx.globalAlpha = fadeOpacity
    ctx.translate(soldier.position.x, soldier.position.y)
    ctx.fillStyle = "#8B7355"
    ctx.beginPath()
    ctx.arc(0, 0, 12 * levelScale, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
  }

  // Selection indicator (amber color to match soldier theme)
  if (isSelected) {
    ctx.save()
    ctx.strokeStyle = "#f59e0b"
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.arc(soldier.position.x, soldier.position.y, 20 * levelScale, 0, Math.PI * 2)
    ctx.stroke()
    ctx.restore()
  }

  // Health bar (smaller than hero/enemy, scales with soldier size)
  const healthBarWidth = 30 * levelScale
  const healthBarHeight = 4
  const healthPercent = soldier.health / soldier.maxHealth
  const barX = soldier.position.x - healthBarWidth / 2
  const barY = soldier.position.y - 28 * levelScale

  // Background
  ctx.fillStyle = "rgba(0, 0, 0, 0.5)"
  ctx.fillRect(barX, barY, healthBarWidth, healthBarHeight)

  // Health fill
  const healthColor = healthPercent > 0.5 ? "#22c55e" : healthPercent > 0.25 ? "#eab308" : "#ef4444"
  ctx.fillStyle = healthColor
  ctx.fillRect(barX, barY, healthBarWidth * healthPercent, healthBarHeight)

  // Border
  ctx.strokeStyle = "#333"
  ctx.lineWidth = 1
  ctx.strokeRect(barX, barY, healthBarWidth, healthBarHeight)

  // Status effects (above health bar, drawn after so they appear on top)
  const soldierStatusEffects = getActiveStatusEffects(soldier)
  if (soldierStatusEffects.length > 0) {
    const iconSize = 12
    const iconGap = 2

    // Calculate positions matching soldier coordinate system
    const statusY = barY - 14 // Icon height (12px) + 2px gap above health bar
    const startX = barX // Left-aligned with health bar

    soldierStatusEffects.forEach((effect, index) => {
      const img = loadStatusEffectIcon(effect.iconPath)
      if (img.complete && img.naturalWidth > 0) {
        const x = startX + index * (iconSize + iconGap)
        ctx.drawImage(img, x, statusY, iconSize, iconSize)
      }
    })
  }
}

// Rally point image cache
let rallyPointImage: HTMLImageElement | null = null

const loadRallyPointImage = (): HTMLImageElement => {
  if (!rallyPointImage) {
    rallyPointImage = new Image()
    rallyPointImage.src = "/images/towers/barracks/barracks_rallypoint.png"
  }
  return rallyPointImage
}

/**
 * Draw the rally point flag for a barracks
 */
export const drawRallyPoint = (
  ctx: CanvasRenderingContext2D,
  position: Position,
  isSelected: boolean = false,
): void => {
  const img = loadRallyPointImage()

  if (img.complete && img.naturalWidth > 0) {
    ctx.save()

    // If selected, draw a pulsing ring FIRST (behind the image), 10px south of position
    if (isSelected) {
      const ringY = position.y + 10
      const pulsePhase = (gameNow() % 1000) / 1000
      const pulseRadius = 12 + Math.sin(pulsePhase * Math.PI * 2) * 3
      ctx.strokeStyle = `rgba(255, 165, 0, ${0.6 + Math.sin(pulsePhase * Math.PI * 2) * 0.3})`
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.arc(position.x, ringY, pulseRadius, 0, Math.PI * 2)
      ctx.stroke()
    }

    // Draw rally point image preserving aspect ratio (50% smaller)
    const targetWidth = 16
    const aspectRatio = img.naturalHeight / img.naturalWidth
    const targetHeight = targetWidth * aspectRatio

    ctx.drawImage(
      img,
      position.x - targetWidth / 2,
      position.y - targetHeight / 2,
      targetWidth,
      targetHeight,
    )

    ctx.restore()
  } else {
    // Fallback: simple flag shape (also 50% smaller)
    ctx.save()
    ctx.fillStyle = "#ffd700"
    ctx.beginPath()
    ctx.moveTo(position.x, position.y - 8)
    ctx.lineTo(position.x + 5, position.y - 5)
    ctx.lineTo(position.x, position.y - 2)
    ctx.closePath()
    ctx.fill()

    ctx.strokeStyle = "#8B4513"
    ctx.lineWidth = 1.5
    ctx.beginPath()
    ctx.moveTo(position.x, position.y - 8)
    ctx.lineTo(position.x, position.y + 4)
    ctx.stroke()
    ctx.restore()
  }
}

/**
 * Draw respawn progress bar for a dead soldier
 * @param barIndex - Which bar (0 or 1) for stacking multiple respawn bars
 */
export const drawSoldierRespawnBar = (
  ctx: CanvasRenderingContext2D,
  towerPosition: Position,
  progress: number, // 0-1
  barIndex: number,
): void => {
  withOverlayScale(ctx, towerPosition, () => drawSoldierRespawnBarInner(ctx, towerPosition, progress, barIndex))
}

const drawSoldierRespawnBarInner = (
  ctx: CanvasRenderingContext2D,
  towerPosition: Position,
  progress: number, // 0-1
  barIndex: number,
): void => {
  const barWidth = 32
  const barHeight = 4
  const barSpacing = 6

  // Position bars below the tower
  const barX = towerPosition.x - barWidth / 2
  const barY = towerPosition.y + 20 + barIndex * barSpacing

  ctx.save()

  // Background (black)
  ctx.fillStyle = "rgba(0, 0, 0, 0.8)"
  ctx.fillRect(barX, barY, barWidth, barHeight)

  // Progress fill (white)
  ctx.fillStyle = "#ffffff"
  ctx.fillRect(barX, barY, barWidth * progress, barHeight)

  // Border
  ctx.strokeStyle = "#333"
  ctx.lineWidth = 1
  ctx.strokeRect(barX, barY, barWidth, barHeight)

  ctx.restore()
}

/**
 * Draw rally point placement radius indicator
 */
export const drawRallyPlacementRadius = (
  ctx: CanvasRenderingContext2D,
  towerPosition: Position,
  radius: number,
  mousePosition?: Position,
): void => {
  ctx.save()

  // Draw radius circle
  ctx.beginPath()
  ctx.arc(towerPosition.x, towerPosition.y, radius, 0, Math.PI * 2)
  ctx.fillStyle = "rgba(255, 215, 0, 0.1)"
  ctx.fill()
  ctx.strokeStyle = "rgba(255, 215, 0, 0.5)"
  ctx.lineWidth = 2
  ctx.setLineDash([5, 5])
  ctx.stroke()

  // If mouse is in range, draw a preview marker
  if (mousePosition) {
    const dx = mousePosition.x - towerPosition.x
    const dy = mousePosition.y - towerPosition.y
    const dist = Math.sqrt(dx * dx + dy * dy)

    if (dist <= radius) {
      // Draw preview at mouse position
      ctx.beginPath()
      ctx.arc(mousePosition.x, mousePosition.y, 8, 0, Math.PI * 2)
      ctx.fillStyle = "rgba(255, 215, 0, 0.5)"
      ctx.fill()
    }
  }

  ctx.restore()
}

// ============================================
// SOLDIER SPAWN PORTRAIT RENDERING
// ============================================

// Cache for soldier portrait spritesheet
const soldierPortraitImages: Record<string, HTMLImageElement> = {}
const SOLDIER_PORTRAIT_SPRITESHEET = "/images/towers/barracks/portraits/soldiers.png"
const SOLDIER_PORTRAIT_SPRITE_SIZE = 64 // Source sprite size

// Preload the soldier portrait spritesheet (client-side only)
const preloadSoldierPortraitSheet = (): void => {
  if (typeof window === "undefined") return // Skip on server
  if (!soldierPortraitImages[SOLDIER_PORTRAIT_SPRITESHEET]) {
    const img = new window.Image()
    img.src = SOLDIER_PORTRAIT_SPRITESHEET
    img.crossOrigin = "anonymous"
    soldierPortraitImages[SOLDIER_PORTRAIT_SPRITESHEET] = img
  }
}

// Auto-preload on module load (will be skipped on server)
preloadSoldierPortraitSheet()

/**
 * Draw a soldier spawn portrait floating up from barracks tower
 * Returns true if portrait is still active, false if expired
 */
export const drawSoldierSpawnPortrait = (
  ctx: CanvasRenderingContext2D,
  towerPosition: Position,
  barrackLevel: number,
  startTime: number,
): boolean => {
  const DURATION = 3000 // 3 seconds
  const RISE_DISTANCE = 60 // pixels to float up
  const DISPLAY_SIZE = 32 // render at 32x32

  const elapsed = gameNow() - startTime
  if (elapsed >= DURATION) return false // expired

  const progress = elapsed / DURATION

  // Fade in first 20%, full opacity middle, fade out last 20%
  let opacity: number
  if (progress < 0.2) {
    opacity = progress / 0.2
  } else if (progress > 0.8) {
    opacity = (1 - progress) / 0.2
  } else {
    opacity = 1
  }

  // Float upward
  const yOffset = -RISE_DISTANCE * progress

  // Position above barracks tower
  const x = towerPosition.x - DISPLAY_SIZE / 2
  const y = towerPosition.y - 40 + yOffset // Start 40px above tower center

  const img = soldierPortraitImages[SOLDIER_PORTRAIT_SPRITESHEET]
  if (!img || !img.complete) return true // still active, wait for image

  ctx.save()
  ctx.globalAlpha = opacity

  // Sprite index is barracks level - 1 (level 1 = index 0, level 7 = index 6)
  const spriteIndex = Math.max(0, Math.min(6, barrackLevel - 1))
  const srcX = spriteIndex * SOLDIER_PORTRAIT_SPRITE_SIZE
  const srcY = 0

  ctx.imageSmoothingEnabled = false
  ctx.drawImage(
    img,
    srcX, srcY, SOLDIER_PORTRAIT_SPRITE_SIZE, SOLDIER_PORTRAIT_SPRITE_SIZE,
    x, y, DISPLAY_SIZE, DISPLAY_SIZE
  )

  ctx.restore()
  return true // still active
}
